/* ============================================
   QUOTE WORKSTATION — Boot
   Asset: workstation-boot.js
   Initialization, designer import, auto-save, modals, validation
   ============================================ */
(function() {
  'use strict';
  var Q = window.QWS;
  if (!Q) return;
  var state = Q.state;
  var dom = Q.dom;
  var wrap = Q.wrap;
  var API = Q.API;
  var Auth = Q.Auth;
  var isAdmin = Q.isAdmin;
  var showToast = Q.showToast;
  var genId = Q.genId;
  var fmt = Q.fmt;
  var parseMoney = Q.parseMoney;
  var escHtml = Q.escHtml;
  var escAttr = Q.escAttr;
  var findItem = Q.findItem;
  var syncCustomSelect = Q.syncCustomSelect;
  var autoPoints = Q.autoPoints;
  var normalizeOptions = Q.normalizeOptions;

  /* ══ CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (2026-08-31) — THE WEIGHT DESIGNER'S CATALOGUE ═══════════
   *
   * The Weight Designer hands over an order in ITS OWN vocabulary: `category|kind` plus a number of
   * pounds. It carries no SKU, no handle and no price, and it deliberately never will. Two reasons,
   * and the second is the load-bearing one:
   *
   *   1. The catalogue is not that app's subject. A product there is a shape to render.
   *   2. ⛔ EVERY ONE OF THESE HANDLES BEGINS WITH THE HOUSE BRAND'S NAME, and that name is still
   *      gated from buyers by `brand-flag.js` (`BRAND_PUBLIC = false`) because the product pages
   *      are not published. The designer's build has a tripwire for that leak, but it scans the
   *      four HELP outputs only, never the app bundle. So a handle table living over there would
   *      ship the gated name to every visitor with the guard still reporting green. It lives here
   *      instead, in a rep-only surface, next to the `/product-by-handle` call that consumes it.
   *
   * ⚠ THE MAP IS BY `category|kind` WITH NO INSTANCE SUFFIX. A plate picked twice in two colours is
   * two ROWS but one PRODUCT; the sender strips the `#2` before it gets here.
   *
   * ⛔ THREE BARS HAVE NO HANDLE ON PURPOSE. The factory answered on 2026-08-31: "We didn't set up
   * the single SKUs of the other barbells. If you have customer request, we will send over via
   * quote." So no individual part number exists for them, they are not built as Shopify products,
   * and Ryan's ruling is that the designer keeps offering all four bar shapes while these three
   * arrive on the quote as unpriced rows the rep quotes by hand. A null handle is the record of a
   * settled answer, not of a missing lookup. */
  var WD_VENDOR = 'Ballast Strength';
  var WD_CATALOG = {
    'bumper|Competition Urethane': { line: 'BS-CUP', unit: 'each', handle: 'ballast-strength-custom-competition-urethane-bumper-plate', title: 'Ballast Strength Custom Competition Urethane Bumper Plate' },
    'bumper|Competition Rubber':   { line: 'BS-CRP', unit: 'each', handle: 'ballast-strength-custom-competition-rubber-bumper-plate',   title: 'Ballast Strength Custom Competition Rubber Bumper Plate' },
    'bumper|Training Rubber':      { line: 'BS-TRP', unit: 'each', handle: 'ballast-strength-custom-training-rubber-bumper-plate',      title: 'Ballast Strength Custom Training Rubber Bumper Plate' },
    'grip|Premium Urethane':       { line: 'BS-UGP', unit: 'each', handle: 'ballast-strength-custom-urethane-grip-plate',               title: 'Ballast Strength Custom Urethane Grip Plate' },
    'dumbbell|Round Rubber':       { line: 'BS-RRD', unit: 'pair', handle: 'ballast-strength-custom-round-rubber-dumbbell',             title: 'Ballast Strength Custom Round Rubber Dumbbell' },
    'dumbbell|Round Urethane':     { line: 'BS-RUD', unit: 'pair', handle: 'ballast-strength-custom-round-urethane-dumbbell',           title: 'Ballast Strength Custom Round Urethane Dumbbell' },
    'barbell|Straight Bar Rubber': { line: 'BS-SRB', unit: 'each', handle: 'ballast-strength-custom-rubber-straight-fixed-barbell',     title: 'Ballast Strength Custom Rubber Straight Fixed Barbell' },
    'barbell|EZ Curl Bar Rubber':    { line: 'BS-ERB', unit: 'each', handle: null, quoteOnly: true, title: 'Ballast Strength Custom Rubber EZ Curl Fixed Barbell' },
    'barbell|Straight Bar Urethane': { line: 'BS-SUB', unit: 'each', handle: null, quoteOnly: true, title: 'Ballast Strength Custom Urethane Straight Fixed Barbell' },
    'barbell|EZ Curl Bar Urethane':  { line: 'BS-EUB', unit: 'each', handle: null, quoteOnly: true, title: 'Ballast Strength Custom Urethane EZ Curl Fixed Barbell' }
  };

  /* ⛔ THE COMPETITION RUBBER 10 LB IS A DIFFERENT PRODUCT, and this is the documented sales
     answer rather than a data error. There is no 10 lb rubber competition plate on the price list;
     the urethane one is what a rubber-competition buyer actually receives at that denomination
     (Ryan 2026-08-02: "it's the one we'll sell as the 10 lber if someone orders the rubber
     competition and they want 10's with that too"). The designer shows the rung because the buyer
     will own that plate. A quote must therefore name the SKU the warehouse will pick, and say why,
     or the rep re-types a line that does not exist. */
  var WD_SUBSTITUTIONS = {
    'bumper|Competition Rubber|10': {
      key: 'bumper|Competition Urethane',
      note: 'Supplied as the competition urethane 10 lb: there is no 10 lb rubber competition plate'
    }
  };

  /* `5` -> `005`, `7.5` -> `007.5`, `150` -> `150`. Mirrors the Blue Sky SKU exactly as
     `sku-map.json` writes it; the pricing back-fill below matches a variant on this string, so a
     format drift here silently prices a row at zero rather than failing loudly. */
  function wdSkuSuffix(weight) {
    var n = Number(weight);
    var whole = Math.floor(n);
    var pad = String(whole);
    while (pad.length < 3) pad = '0' + pad;
    return pad + (n > whole ? '.5' : '');
  }

  /* Drop the leading vendor name, so a row reads "Custom Round Rubber Dumbbell" under a heading
     that already says who makes it. Derived from `WD_VENDOR` rather than a second literal, so the
     brand stays one decision in this file. */
  function wdShortTitle(title) {
    var t = String(title || '');
    return t.indexOf(WD_VENDOR + ' ') === 0 ? t.slice(WD_VENDOR.length + 1) : t;
  }

  function wdWeightLabel(weight) {
    return String(weight) + ' lb';
  }

  /* The "+ Weight Series" modal's own SKU pattern: `5` -> `005`, `7.5` -> `7_5`. Kept for series a
     rep builds by hand; a catalogue-backed series uses wdSkuSuffix (the real SKU) instead. Lives up
     here with its sibling so `seriesRungFrom` can be sliced out of this file and tested. */
  function zeroPad(w) {
    var n = parseFloat(w);
    if (n !== Math.floor(n)) return String(w).replace('.', '_');
    if (n < 10) return '00' + n;
    if (n < 100) return '0' + n;
    return String(n);
  }

  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-08, RW-1387-A) — A RUNG ADDED TO A SERIES IS
     THE SAME PRODUCT AS ITS SIBLINGS. The customer asked to grow their 5-20 lb dumbbell set to
     5-50; Ryan clicked "+ add weight" under the Ballast Strength series, picked 25-50, and got a
     SECOND vendor group headed "CUSTOM" with no MAP and no cost. The modal inherited the title
     base, the SKU base and the points from a sibling and then hard-coded `vendor: 'Custom'`,
     `msrp: 0`, `cost: 0`, `mapPrice: 0` and the modal's own SKU pattern for the rest — so every
     added rung was a stranger to the ladder it was added to. That was true of a hand-built
     series too (the vendor prompt renames the first rungs; later ones landed under "Custom").

     ⛔ ONE PLACE DECIDES WHAT A NEW RUNG LOOKS LIKE, and it reads the SIBLING, never a literal:
       - vendor, type, seriesIsPlates, productHandle: the sibling's (the group heading follows).
       - sku: a catalogue-backed series (productHandle set) uses the REAL SKU shape,
         `base-wdSkuSuffix(w)` (`BS-RRD-025`), which is the string the pricing back-fill matches a
         variant on; a hand-built series keeps the modal's `base-zeroPad(w)`.
       - title: the sibling's full title with its weight label swapped for the new one when it
         follows the handoff's `<unit title> - <N lb>` shape; else the modal's `<base> – <N lbs>`.
       - msrp: the sibling's `-1` "N/A" sentinel carries (Ballast has no compare-at price by
         decision — see wdRowsFor); a 0 would paint the cell red and block Send Quote.
       - cost / mapPrice / directPrice start at 0 and the pricing back-fill fills them from the
         product's own variant, exactly as the handoff's rows are priced.
     Pure: returns the row minus `id` (the caller stamps `genId()`), so it can be tested from the
     shipped file the way wdRowsFor is. */
  function seriesRungFrom(sibling, weight, seriesId) {
    var s = sibling || {};
    var w = Number(weight);
    var baseTitle = s.seriesBaseTitle || '';
    var baseSku = s.seriesBaseSku || '';
    var handle = s.productHandle || s._productHandle || null;
    var sku = baseSku ? (baseSku + '-' + (handle ? wdSkuSuffix(w) : zeroPad(w))) : '';
    var title = baseTitle ? (baseTitle + ' – ' + w + ' lbs') : '';
    if (s.title && s.seriesWeight != null) {
      var tail = ' - ' + wdWeightLabel(s.seriesWeight);
      var t = String(s.title);
      if (t.length > tail.length && t.slice(-tail.length) === tail) title = t.slice(0, -tail.length) + ' - ' + wdWeightLabel(w);
    }
    return {
      type: s.type || 'custom',
      variantId: null,
      vendor: s.vendor || 'Custom',
      title: title,
      sku: sku,
      quantity: 0,
      msrp: s.msrp === -1 ? -1 : 0,
      cost: 0,
      points: s.points || 30,
      directPrice: 0,
      mapPrice: 0,
      isFree: false,
      subDescriptions: [],
      internalNote: '',
      seriesId: seriesId,
      seriesWeight: w,
      seriesBaseTitle: baseTitle || null,
      seriesBaseSku: baseSku || null,
      seriesIsPlates: !!s.seriesIsPlates,
      productHandle: handle,
      _productHandle: handle,
      configured: s.configured !== false
    };
  }

  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-08, "build it") — A HOUSE-BRAND LINE'S REAL
     WEIGHT LADDER, read off the product's own variants. Every Ballast product is weight-only
     (one option, `Weight`), and the SKU is `<line>-<wdSkuSuffix(weight)>`, so the suffix IS the
     weight: `BS-RRD-007.5` -> 7.5, `BS-UGP-002.5` -> 2.5. A variant whose SKU is not on this line
     is ignored rather than guessed at. Pure; sorted ascending, de-duplicated. */
  function wdLadderFrom(entry, product) {
    var out = [];
    var prefix = entry && entry.line ? entry.line + '-' : null;
    var variants = product && Array.isArray(product.variants) ? product.variants : [];
    if (!prefix) return out;
    variants.forEach(function(v) {
      var sku = String((v && v.sku) || '');
      if (sku.indexOf(prefix) !== 0) return;
      var w = parseFloat(sku.slice(prefix.length));
      if (!(w > 0)) return;
      w = Math.round(w * 100) / 100;
      if (out.indexOf(w) === -1) out.push(w);
    });
    out.sort(function(a, b) { return a - b; });
    return out;
  }

  /* The catalogue lines a rep may build a series from: every WD_CATALOG entry with a product
     behind it (a quote-only bar has no variants to price from). `[{key, entry, label}]`. */
  function wdPickableLines() {
    return Object.keys(WD_CATALOG).filter(function(k) { return !!WD_CATALOG[k].handle; })
      .map(function(k) { return { key: k, entry: WD_CATALOG[k], label: wdShortTitle(WD_CATALOG[k].title) }; });
  }

  /* THE PRICING BACK-FILL, one implementation for every row that arrives with a product handle:
     the designer handoff (`applyImportedItems`) and a rung added to an existing series through the
     "+ add weight" modal (2026-09-08 — until then the modal's rows had no handle, so nothing ever
     priced them and MAP / cost sat empty). `needsPricing` is `[{handle, sku, itemId}]`.

     ⛔ ONE FETCH PER PRODUCT, NOT PER ROW. Every rung of a line shares a handle, and since a rep
     can hand over a whole ladder at quantity 0 (WD 368), a single dumbbell is 35 rows — which was
     35 identical requests for one answer, three at a time with a 150 ms pause between batches.
     The variant is still matched per ROW, by its own SKU, against the one response.

     ⛔ A PRICING FAILURE IS SAID OUT LOUD, ONCE, NAMING THE ROWS. The per-handle catch used to be a
     bare `.catch(function() {})`, so a 401, a 404 on a renamed handle, a Shopify 5xx and a dropped
     connection all produced the same outcome: a row sitting at $0 that looks exactly like a
     deliberately unpriced quote-only row. `opts.failures` lets a caller pass its own collector;
     `opts.afterPricing` fires once every batch has finished. */
  function backfillPricing(needsPricing, opts) {
    opts = opts || {};
    var pricingFailures = opts.failures || [];
    var handleOrder = [];
    var byHandle = Object.create(null);
    (needsPricing || []).forEach(function(np) {
      if (!np || !np.handle) return;
      if (!byHandle[np.handle]) { byHandle[np.handle] = []; handleOrder.push(np.handle); }
      byHandle[np.handle].push(np);
    });
    console.log('[WS Pricing] ' + (needsPricing || []).length + ' rows across '
      + handleOrder.length + ' product' + (handleOrder.length === 1 ? '' : 's'));

    var BATCH = 3;
    (function fetchBatch(start) {
      if (start >= handleOrder.length) {
        Q.renderItems();
        if (pricingFailures.length && Q.showAlert) {
          /* CUSTOM_QWALERTS_BY_CLAUDE (Ryan 2026-10-23, Pass 2) - a red row that stays until dismissed, not a 9-second toast. */
          Q.showAlert({ id: 'unpriced', level: 'red', icon: '&#x1F4B2;', persist: false, quoteScoped: true,
            head: pricingFailures.length + ' line' + (pricingFailures.length === 1 ? '' : 's') + ' at $0',
            text: 'Could not be priced: ' + pricingFailures.join(', ') + '. Fill in cost and price on ' + (pricingFailures.length === 1 ? 'that row' : 'those rows') + ' before sending.' });
        } else if (pricingFailures.length) {
          showToast(pricingFailures.length + ' line' + (pricingFailures.length === 1 ? '' : 's')
            + ' could not be priced and ' + (pricingFailures.length === 1 ? 'is' : 'are')
            + ' sitting at $0: ' + pricingFailures.join(', ')
            + '. Fill in cost and price on those rows before sending.', 'error', 9000);
        }
        if (typeof opts.afterPricing === 'function') opts.afterPricing();
        return;
      }
      var batch = handleOrder.slice(start, start + BATCH);
      Promise.all(batch.map(function(handle) {
        return API.get('/product-by-handle?handle=' + encodeURIComponent(handle)).then(function(data) {
          var product = data.product;
          if (!product || !product.variants) return;
          byHandle[handle].forEach(function(np) {
            var variant = null;
            if (np.sku) {
              for (var vi = 0; vi < product.variants.length; vi++) {
                if (product.variants[vi].sku === np.sku) { variant = product.variants[vi]; break; }
              }
            }
            /* ⚠ NO FALLBACK TO variants[0]. With one response feeding MANY rows, a SKU that fails
               to match would silently price every unmatched rung at the FIRST variant's figures —
               the lightest plate's price on a 55 lb row. A miss is reported instead. */
            if (!variant) {
              pricingFailures.push(np.sku || handle);
              console.warn('[WS Pricing] no variant matched SKU ' + np.sku + ' on ' + handle);
              return;
            }
            var item = findItem(np.itemId);
            if (!item) return;
            item.cost = parseFloat(variant.cost) || 0;
            item.msrp = parseFloat(variant.compareAtPrice) || item.msrp;
            item.mapPrice = parseFloat(variant.price) || 0;
            item.variantId = variant.id;
            if (item.cost > 0 && item.mapPrice > 0) {
              item.points = autoPoints(item.cost, item.mapPrice);
              item.directPrice = item.mapPrice;
            }
            if (variant.selectedOptions && product.options) {
              var optList = normalizeOptions(product.options);
              variant.selectedOptions.forEach(function(so) {
                var optDef = optList.find(function(o) { return o.name === so.name; });
                if (optDef) {
                  var vals = (optDef.values || []).map(function(v) { return typeof v === 'string' ? v : v.name; });
                  Q.savePref(item.vendor, so.name, vals, so.value);
                }
              });
            }
          });
        }).catch(function(err) {
          /* The whole product failed, so every row waiting on it is unpriced — name them all
             rather than the handle, because the rep reads rows, not handles. */
          (byHandle[handle] || []).forEach(function(np) { pricingFailures.push(np.sku || handle); });
          console.warn('[WS Pricing] failed for ' + handle + ':', (err && err.message) || err);
        });
      })).then(function() {
        Q.renderItems();
        validateAllRows();
        setTimeout(function() { fetchBatch(start + BATCH); }, 150);
      });
    })(0);
  }

  /* Turn the designer's `weights` groups into Quote Writer rows.
     `[{ product, key, unit, label, one, configuration, lines:[{weight, qty}] }]` -> `[item]` */
  function wdRowsFor(groups) {
    /* ⛔ ONE WEIGHT SERIES PER PRODUCT LINE (Ryan 2026-09-01): *"If we're sending urethane
       dumbbells, I want them all in a series.. rubber dumbbells = series.. urethane grip plates =
       series. training bumpers = series, etc."* A ladder is one product at many weights, which is
       exactly what the Quote Writer's weight series already models — shared `seriesId`, a base
       title and base SKU, one row per rung. Sending them as loose `type:'custom'` rows made the rep
       re-learn on every quote that thirty-five lines were one thing.

       ⚠ GROUPED BY THE RESOLVED LINE, NOT THE PRODUCT THE REP PICKED. A Competition Rubber order
       containing a 10 lb resolves that rung to BS-CUP, a genuinely different product — so it joins
       the urethane series (or stands as its own) rather than sitting inside the rubber ladder
       carrying a SKU from another line. */
    var out = [];
    var order = [];
    var byLine = Object.create(null);

    var skipped = [];
    (groups || []).forEach(function(group) {
      if (!group || !Array.isArray(group.lines)) return;
      group.lines.forEach(function(line) {
        var weight = Number(line && line.weight);
        var qty = Math.max(0, Math.trunc(Number(line && line.qty) || 0));
        /* ⛔ A ZERO QUANTITY IS A ROW, NOT AN ABSENCE. The count is what the rep fills in at the
           Workstation; the WEIGHT is what resolves the SKU and the price, so only that is required. */
        if (!(weight > 0)) return;

        var sub = WD_SUBSTITUTIONS[group.product + '|' + weight];
        var entry = WD_CATALOG[(sub && sub.key) || group.product];
        /* ⛔ SKIPPED, AND NOW SAID OUT LOUD. A product the catalogue has never heard of is not
           guessed at — but this used to be a silent `return`, so a whole product could vanish
           between the customer's order and the rep's quote with nothing anywhere saying it had.
           Collected and reported by the caller. */
        if (!entry) {
          if (skipped.indexOf(group.product) === -1) skipped.push(group.product);
          return;
        }

        /* One bucket per resolved line AND colour: two configurations of a plate are two series,
           because they are two different things on the quote even though they share a SKU. */
        var bucket = entry.line + '|' + (group.configuration || '');
        if (!byLine[bucket]) {
          byLine[bucket] = { entry: entry, configuration: group.configuration || null, notes: [], rows: [] };
          order.push(bucket);
        }
        var g = byLine[bucket];

        /* ⚠ PRODUCT-LEVEL NOTES, COLLECTED HERE AND ATTACHED ONCE. The Workstation renders
           sub-text on a series' LAST row only (`isSeriesLast`), so a note repeated on every rung
           would be shown once anyway and a note on a middle rung would never be seen at all. The
           substitution note is reworded from "Supplied as" to "Includes" for the same reason: it
           now describes the series rather than the single rung it came from. */
        if (sub && g.notes.indexOf(sub.note) === -1) g.notes.push(sub.note);

        /* ⛔ "Pair" IS PART OF THE NAME, NOT A FOOTNOTE (Ryan 2026-09-01). A dumbbell variant IS a
           pair and its quantity counts pairs, so a rep reading "3" must not price three bells -
           the one error on this brand that misquotes a rack by exactly 2x. It used to be said in
           the sub-text, where a series shows it once at the bottom of thirty-five rows; saying it
           in the title says it on every row, in the PDF, and on the purchase order.
           ⚠ IN `title` AS WELL AS THE DISPLAY NAME, deliberately. `title` is what the PO and the
           quote PDF print, and "45 lb Dumbbell" on a document going to a factory is a different
           order from "45 lb Dumbbell Pair". */
        var unitTitle = entry.unit === 'pair' ? entry.title + ' Pair' : entry.title;

        g.rows.push({
          type: 'custom',
          title: unitTitle + ' - ' + wdWeightLabel(weight),
          sku: entry.line + '-' + wdSkuSuffix(weight),
          quantity: qty,
          vendor: WD_VENDOR,
          /* ⛔ NO MSRP, AND THAT IS A DECISION RATHER THAN A GAP. `build_ballast_line.py` omits
             `compareAtPrice` on every Ballast variant on purpose - a permanent strikethrough is a
             Google Merchant misrepresentation risk - so the pricing back-fill has nothing to fill
             this with and would leave it 0. A 0 MSRP is what `validateRow` paints red, and a red
             cell BLOCKS Preview PDF and Send Quote. `-1` is the Workstation's own "N/A" sentinel
             (what typing `-` into the cell sets): it renders as `-`, is excluded from the MSRP
             total, and clears the flag. ⚠ The back-fill reads `parseFloat(compareAtPrice) ||
             item.msrp`, so a real compare-at price would still win over this seed. */
          msrp: -1,
          subDescriptions: [],
          /* ── the weight series ── */
          seriesWeight: weight,
          /* ⛔ THE BRAND IS ON THE GROUP, NOT ON THIRTY-FIVE ROWS (Ryan 2026-09-01: *"I don't need
             'Ballast Strength' for every line item. Just for the brand/category is fine"*). The
             Workstation already groups by vendor and prints that name as the group's heading, so
             repeating it down the ladder is the heading said thirty-five more times.
             ⚠ STRIPPED FROM THE DISPLAY TITLE ONLY. `title` keeps the full name because it is an
             IDENTITY downstream - `wsByTitle` matches on it, and the PO and the quote PDF each
             strip the vendor prefix themselves for exactly this reason. Two different jobs. */
          seriesBaseTitle: wdShortTitle(unitTitle),
          /* The base of the REAL SKU, not the modal's `zeroPad` pattern: these come from
             `sku-map.json` and `BS-RRD-007.5` is the string the warehouse picks by. */
          seriesBaseSku: entry.line,
          seriesIsPlates: entry.line !== 'BS-RRD' && entry.line !== 'BS-RUD',
          /* ⛔ BOTH SPELLINGS, DELIBERATELY. `applyImportedItems` reads `_productHandle` and
             `Q.addItem` stores `productHandle`; the Gym Layout Designer writes both for exactly
             this reason. A row carrying only one gets no pricing back-fill and no warning. */
          productHandle: entry.handle || null,
          _productHandle: entry.handle || null,
          configured: true
        });
      });
    });

    order.forEach(function(bucket) {
      var g = byLine[bucket];
      if (!g.rows.length) return;
      var seriesId = 'wdser_' + Math.random().toString(36).slice(2, 11);
      // Ascending, because the Workstation puts the series' sub-text on its HIGHEST weight.
      g.rows.sort(function(a, b) { return a.seriesWeight - b.seriesWeight; });

      /* ⛔ ON EVERY SERIES, FIRST (Ryan 2026-09-01). The artwork is the whole product and the
         commonest question a quote provokes is whether the printing costs extra. Saying it once per
         product beats a rep remembering to type it, and it is the same claim `preflight_content.py`
         already guards on the product pages, so the quote and the page cannot disagree.
         No em dashes: this is customer copy. */
      var notes = ['Custom design included in price'];
      if (g.configuration) notes.push('Plate color: ' + g.configuration);
      /* ⚠ NO PAIR NOTE ANY MORE, and that is safe only because the TITLE now carries the word.
         "Sold as a pair, quantity is in pairs" moved into the product name above, where a series
         shows it on every row instead of once at the bottom. ⛔ If the title ever stops saying
         "Pair", this note has to come back: the 2x misquote is the single worst error available on
         this brand, and it must be stated somewhere a rep cannot miss. */
      g.notes.forEach(function(n) { notes.push(n); });
      if (g.entry.quoteOnly) notes.push('Quote required: this bar has no individual part number and is quoted per order');

      g.rows.forEach(function(r) { r.seriesId = seriesId; });
      g.rows[g.rows.length - 1].subDescriptions = notes;
      g.rows.forEach(function(r) { out.push(r); });
    });
    /* ⚠️ `{rows, skipped}`, not a bare array — the one call site was updated with it. A product the
       catalogue cannot place is a fact the rep has to be told, and the only place that knows is
       here. */
    return { rows: out, skipped: skipped };
  }

  /* ── Success: New Quote ── */
  var successNew = wrap.querySelector('.rq-ws-success__new');
  if (successNew) {
    successNew.addEventListener('click', function() { Q.resetToNew(); });
  }

  /* ── Close dropdowns on outside click ── */
  document.addEventListener('click', function(e) {
    if (dom.loadDropdown && !dom.loadDropdown.contains(e.target) && e.target !== dom.btnLoad) {
      Q.toggleLoadDropdown(false);
    }
    if (Q.bulkDrop && !Q.bulkDrop.contains(e.target) && e.target !== Q.bulkBtn) {
      Q.bulkDrop.classList.remove('rq-ws-bulk-wrap__dropdown--open');
    }
    // Close search if clicking outside
    if (!dom.searchResults.contains(e.target) && e.target !== dom.searchInput) {
      Q.closeSearch();
    }
  });

  /* ── Check for designer import (localStorage) ── */
  /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — Mode-aware import. Payload may be:
       (legacy) plain array of items → treated as mode='new'
       (current) { mode, baseRef?, targetRef?, items } where mode is 'new' | 'revision' | 'append'
     - 'new'      : current behavior (add items into a fresh quote, rep saves manually)
     - 'append'   : loadQuote(targetRef) → push items into existing line items → trigger save
     - 'revision' : find latest letter for baseRef → loadQuote(latest) → replace items → trigger save-as (clones header into new -X) */
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-01) — the 'existing' mode's deferred body.
     A rep arriving from the Deal Dashboard's Create Quote lands on `?ref=...`, which this page is
     ALREADY loading. The import cannot just run: `loadQuote` clears `state.items`, so rows added
     beside it are wiped by whichever load lands second. Assigned here and fired by `loadRefOnce`
     once that load settles. Null on every other path. */
  var pendingExistingImport = null;

  var importData = localStorage.getItem('rep_workstation_import');
  console.log('[WS Import] Check:', importData ? 'FOUND ' + importData.length + ' bytes' : 'none');
  if (importData) {
    localStorage.removeItem('rep_workstation_import');
    try {
      var parsed = JSON.parse(importData);
      var importMode = 'new';
      var importItems = [];
      var importBaseRef = null;
      var importTargetRef = null;
      var importAsRevision = false;
      var importDesignRef = null;
      var importDesignId = null;
      var importDesignType = null;
      var importDesignStore = null;
      var importIsWeights = false;
      /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-01) — the 'existing' mode's extras: when the
         stash was written (its five-minute freshness window), what the worker said about the
         design, and which product keys the catalogue had no entry for. */
      var importStashedAt = 0;
      var importWdImport = null;
      var importSkipped = [];
      if (Array.isArray(parsed)) {
        importItems = parsed;
      } else if (parsed && parsed.mode && Array.isArray(parsed.items)) {
        importMode = parsed.mode;
        importItems = parsed.items;
        importBaseRef = parsed.baseRef || null;
        importTargetRef = parsed.targetRef || null;
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-11) — append, then save as the next revision. */
        importAsRevision = parsed.asRevision === true;
        importStashedAt = Number(parsed.stashedAt) || 0;
        importWdImport = parsed.wdImport || null;
        importDesignRef = parsed.designRef || null;
        importDesignId = parsed.designId || null;
        /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (2026-08-31) — WHICH KIND OF DESIGN THIS IS.
           Absent from a Gym Layout Designer handoff, and that is correct: the worker's defaults
           reproduce floor-plan behaviour exactly, so the GLD is untouched by this. Present from the
           Weight Designer, because its designs live in a different keyspace and defaulting was a
           guaranteed 404. See the link POST below. */
        importDesignType = parsed.designType || null;
        importDesignStore = parsed.designStore || null;
        /* The Weight Designer's order, in its own vocabulary. Converted to priced rows HERE rather
           than sent as rows, so the house brand's handles never enter the buyer-facing bundle. */
        if (Array.isArray(parsed.weights) && parsed.weights.length) {
          /* ⛔ AND WHAT IT COULD NOT CONVERT. `wdRowsFor` used to drop a product whose
             `category|kind` has no WD_CATALOG entry silently (`if (!entry) return;`) — a whole
             product could vanish between the customer's order and the rep's quote with nothing
             said. It now reports them and the 'existing' branch names them in a toast. */
          var _wd = wdRowsFor(parsed.weights);
          var wdRows = _wd.rows;
          importSkipped = _wd.skipped;
          console.log('[WS Import] weight designer groups=' + parsed.weights.length
            + ' -> rows=' + wdRows.length + (importSkipped.length ? ' skipped=' + importSkipped.join(',') : ''));
          importItems = importItems.concat(wdRows);
          importIsWeights = true;
        }
      }
      /* ⚠ THE CONFIRMATION TOASTS USED TO SAY "Floor plan" NO MATTER WHAT AROSE. This screen was
         built for the Gym Layout Designer and every append/revision toast hardcoded that word, so a
         weight handoff confirmed itself as somebody else's import. */
      var importLabel = (importIsWeights || importDesignType === 'weights') ? 'Weight design' : 'Floor plan';
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-21) — Stash designRef +
         designId on state, then attach a one-shot rq-ws:saved listener that
         POSTs /quote-link-design with the now-known quote ref on the first
         successful save (saveDraft or saveAsDraft both dispatch the event,
         and both set state.ref before dispatching). Hooking the event from
         here keeps the link logic out of every save handler and works
         uniformly across Mode 1 (new quote, manual Save), Mode 2 (revision,
         auto-Save As), and Mode 3 (append, manual Save).

         Null when the rep handed off from an unsaved design — skip the link
         write entirely in that case (we can't reference a design that has
         no portal ref yet). */
      if (importDesignId && importDesignRef) {
        state.pendingLinkDesign = {
          designRef: importDesignRef,
          designId: importDesignId,
          type: importDesignType,
          store: importDesignStore,
        };
        var _linkOnSave = function() {
          document.removeEventListener('rq-ws:saved', _linkOnSave);
          if (!state.pendingLinkDesign || !state.ref) return;
          var p = state.pendingLinkDesign;
          state.pendingLinkDesign = null;
          var linkBody = {
            ref: state.ref,
            designRef: p.designRef,
            designId: p.designId,
          };
          /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (2026-08-31) — ⛔ SENT ONLY WHEN THE SENDER NAMED THEM.
             `/quote-link-design` chooses between three keyspaces on `type` and `store`. Its `type`
             default is a FALSY FALLTHROUGH to 'floor-plan', which then forces `store` to 'design:'.
             This POST used to omit both, so a Weight Designer handoff, whose record lives at
             `wd-design:{id}`, 404'd on every single save from the day it shipped, and the failure
             was swallowed into the console.warn below where nobody would ever see it.
             Adding the fields conditionally keeps the Gym Layout Designer on the defaults it wants
             rather than making it state what it has never had to state. */
          if (p.type) linkBody.type = p.type;
          if (p.store) linkBody.store = p.store;
          /* CUSTOM_BALLAST_BY_CLAUDE (Ryan 2026-09-01) - WHOSE design this is, so a purchase order
             can tell whether the Vendor Design Sheet belongs to the supplier it is being sent to.
             A design sheet carries a customer's artwork, so `lib/vendor-design-sheet.js` fails
             CLOSED and skips any link that does not name a vendor.
             Named HERE rather than in the designer for the same reason `WD_CATALOG` lives here:
             every house-brand string is gated out of the vite bundle, and the help build's
             tripwire scans only its four outputs, so a brand name in `app/src` would ship with the
             guard green. */
          if (p.type === 'weights') linkBody.vendor = WD_VENDOR;
          API.post('/quote-link-design', linkBody).then(function() {
            console.log('[WS] /quote-link-design ok:', state.ref, '←', p.designRef);
          }).catch(function(err) {
            /* ⚠ THIS IS NOW SAID OUT LOUD. A silent console.warn is what let the bug above live
               unnoticed: the rep saw a successful save, the design was not attached, and nothing
               anywhere said so. The save itself really did succeed, so this is a warning about the
               LINK and it names the recovery. */
            console.warn('[WS] /quote-link-design failed:', err && err.message);
            showToast('Quote ' + state.ref + ' saved, but the design could not be attached to it. '
              + 'Attach it from the deal\'s Files panel, or reopen this quote from the designer. ('
              + ((err && err.message) || 'unknown error') + ')', 'warning', 9000);
          });
        };
        document.addEventListener('rq-ws:saved', _linkOnSave);
        // CUSTOM_ROOMDESIGNER_BY_CLAUDE — a design is being linked to this quote,
        // so reveal the "add layout to PDF" toggle right away (loadQuote also
        // refreshes it on the append path once meta.linkedFiles is read).
        if (Q.refreshLayoutPdfToggle) setTimeout(function() { Q.refreshLayoutPdfToggle(); }, 0);
      }
      console.log('[WS Import] mode=' + importMode + ' items=' + importItems.length + (importBaseRef ? ' baseRef=' + importBaseRef : '') + (importTargetRef ? ' targetRef=' + importTargetRef : '') + (importDesignRef ? ' designRef=' + importDesignRef : ''));

      // Helper — add items into state, render, then fetch pricing in batches.
      // opts.afterPricing fires once all pricing batches finish (used to trigger save in modes 2/3).
      function applyImportedItems(items, opts) {
        opts = opts || {};
        var needsPricing = [];
        /* ⛔ A PRICING FAILURE USED TO BE COMPLETELY INVISIBLE. The per-handle catch below was a
           bare `.catch(function() {})`, so a 401, a 404 on a renamed handle, a Shopify 5xx and a
           dropped connection all produced the same outcome: a row sitting at $0 that looks exactly
           like a deliberately unpriced quote-only row. Nothing was logged either, so diagnosing it
           meant opening the Network tab and already knowing to. Collected and said once, at the
           end, naming the rows - the rep cannot act on what they cannot see. */
        var pricingFailures = [];
        items.forEach(function(item) {
          // _skipDupCheck — append mode may bring SKUs already on the target quote;
          // duplicate dialog would block the rest of the batch. Reps can manually
          // dedupe after import if needed.
          Q.addItem(Object.assign({}, item, { _skipRender: true, _skipDupCheck: true }));
          /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-01) — ⛔ AN IMPORTED LADDER ARRIVES
             ALREADY SEEDED, SO IT MUST NOT BE RE-SEEDED. `workstation-table.js` propagates the
             FIRST quantity typed into any rung of a series across every other rung — a genuine
             convenience for a ladder you just built by hand and want 4 of everything in. It is
             destructive for one that came from the Weight Designer, which already carries the
             quantities the customer asked for: RW-1371-A is 4 pairs each of 5-25 lb and 2 pairs
             each of 30-100 lb, and one keystroke in any box would have flattened all twenty rungs
             to whatever was typed, silently, in nineteen rows the rep never touched.
             `seriesQtyPropagated` starts empty at page boot and nothing ever seeded it for an
             import, so the guard could not fire. Marking the series spent here is the whole fix:
             every later edit then touches only the row it is in. */
          if (item.seriesId && Q.seriesQtyPropagated) Q.seriesQtyPropagated[item.seriesId] = true;
          if (item._productHandle) {
            needsPricing.push({ handle: item._productHandle, sku: item.sku, itemId: state.items[state.items.length - 1].id });
          }
        });
        Q.renderItems();
        validateAllRows();
        /* The batch fetch itself is `backfillPricing` (file scope), shared with the "+ add weight"
           modal since 2026-09-08 so a rung added to an imported ladder is priced the same way. */
        backfillPricing(needsPricing, { afterPricing: opts.afterPricing, failures: pricingFailures });
      }

      // Run a function once auth is ready (loadQuote / rep-quotes need a session).
      // Tab opened via window.open() inherits sessionStorage from the RD tab, so
      // getSession() is usually truthy synchronously — the event is the fallback.
      // The auth event dispatches on `window` (custom-rep-portal.js); listen there.
      function whenAuthed(fn) {
        var hasSession = window.RepPortal && window.RepPortal.Auth && window.RepPortal.Auth.getSession && window.RepPortal.Auth.getSession();
        if (hasSession) { fn(); return; }
        window.addEventListener('rep-portal:authenticated', function once() {
          window.removeEventListener('rep-portal:authenticated', once);
          fn();
        });
      }

      /* ⛔ AN EMPTY IMPORT MUST STILL HONOUR THE MODE. This short-circuited on
         `importItems.length === 0` for EVERY mode, which silently discarded the rep's choice:
         picking "new revision in an existing chain" or "append to an existing quote" and then
         arriving with no rows opened a BLANK NEW QUOTE instead, and the one-shot link listener
         registered above then attached the design to whatever fresh ref was saved next. The Weight
         Designer's Create Quote card sits ABOVE the quantity grid, so handing off before entering
         quantities is an easy first move rather than an exotic one. Only 'new' has nothing to do. */
      if (importItems.length === 0 && importMode === 'new') {
        // nothing to do
      } else if (importMode === 'new') {
        applyImportedItems(importItems);
      } else if (importMode === 'append' && importTargetRef) {
        whenAuthed(function() {
          /* CUSTOM_ROOMDESIGNER_BY_CLAUDE (Ryan 2026-06-19) — Lead hydration for
             append-to-existing. When the append target is a LEAD-originated deal
             (e.g. an on-hold sample-request lead like RW-1181-A with no quote
             yet), POST /create-quote-from-lead FIRST — the same handoff the DD's
             "Build Quote from Lead" CTA uses. It creates/reuses the Shopify draft
             with the customer pre-filled + patches workstation_meta
             (leadContext / leadSource / cfaAttribution) and returns customer +
             leadContext, which we stash as bsfs_lead_handoff:{ref} so loadQuote's
             existing lead-handoff path fills Sold To AND the Lead Source / CFA /
             Inbound Lead Source dropdowns. 404/caught for non-lead targets, which
             then load exactly as before (incl. the PENDING fallback below). */
          /* CUSTOM_ROOMDESIGNER_BY_CLAUDE (Ryan 2026-07-01) — First-open blank
             Sold To fix. This POST's failure was silently swallowed, and with it
             the bsfs_lead_handoff stash that instant-fills Sold To — one Shopify
             hiccup left the rep a blank customer card under a green success
             toast. Retry transient failures (network error / 5xx) twice with
             backoff; a 404 ("not a lead" / already a real quote) is the designed
             fast path and never retries. */
          (function createFromLead(attempt) {
            return API.post('/create-quote-from-lead', { ref: importTargetRef }).catch(function(err) {
              var transient = !err || !err.status || err.status >= 500;
              if (transient && attempt < 2) {
                return new Promise(function(res) { setTimeout(res, attempt === 0 ? 1000 : 2500); })
                  .then(function() { return createFromLead(attempt + 1); });
              }
              throw err;
            });
          })(0).then(function(res) {
            if (res && res.quoteRef && (res.customer || res.leadContext)) {
              try {
                sessionStorage.setItem('bsfs_lead_handoff:' + res.quoteRef, JSON.stringify({
                  customer: res.customer || null,
                  leadContext: res.leadContext || null,
                  draftOrderId: res.draftOrderId || null,
                  draftOrderName: res.draftOrderName || null,
                  stashedAt: Date.now(),
                }));
              } catch (e) { /* storage quota/disabled — loadQuote retries via /quote-details */ }
            }
          }).catch(function(err) {
            // not a lead / already a quote — load as normal. Log real failures
            // so a blank Sold To is diagnosable from the console.
            if (err && err.status !== 404) console.warn('[WS Import] /create-quote-from-lead failed:', err.message || err);
          }).then(function() {
          return Q.loadQuote(importTargetRef).then(function() {
            /* CUSTOM_ROOMDESIGNER_BY_CLAUDE (ONYX a328b43a7755, Ryan 2026-10-13) — ⛔ A LOAD THAT FOUND
               NO QUOTE IS A FAILURE. `loadQuote` catches its own 404 and RESOLVES, so the PENDING
               fallback in the .catch below never ran: RW-1504-A (a pending deal with only Sold To /
               Ship To) got the floor plan's rows on a BLANK customer form, and Save wrote the blank
               over the deal. No draft id after the load means nothing loaded; reject so the pending
               record fills the form first. tests/ws-append-pending-tests.mjs. */
            if (!state.draftOrderId) { var _noQuote = new Error('Quote not found'); _noQuote.status = 404; throw _noQuote; }
            applyImportedItems(importItems, { afterPricing: function() {
              // Don't auto-save — let the rep review the merged items and decide.
              // Closing the tab without saving = target quote unchanged.
              state.suppressDirty = false; state.dirty = true;
              if (Q.updateSaveButton) Q.updateSaveButton();
              /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-11) — ⛔ APPEND-AND-SAVE-AS IS ONE MOVE NOW.
                 Ryan: *"what reps will do is append to that quote, then immediately 'Save as' - so
                 maybe this is how you do it, just combine those things somehow from the GLD."*
                 Both halves already existed and this joins them: the quote is loaded WITH its own
                 items (unlike the revision path, which blanks them), the floor plan merges on top,
                 and Save As mints the next letter through /clone-quote. The target revision is
                 never written to, so adding to a quote the customer already has stops being a
                 destructive act.
                 ⚠️ The rep still reviews first if Save As is missing or its validation fails —
                 exactly as the revision path behaves, and the merged items are already on screen. */
              if (importAsRevision) {
                var saveAsBtn = document.querySelector('.rq-ws-send__save-as');
                if (saveAsBtn) {
                  showToast(importLabel + ' items merged into ' + importTargetRef + ' - saving as the next revision...', 'success');
                  saveAsBtn.click();
                  return;
                }
                showToast(importLabel + ' items merged into ' + importTargetRef + '. Click Save As to create the next revision.', 'success');
                return;
              }
              /* Ryan 2026-05-21: 'success' kind → green toast. The success
                 message here was inheriting showToast's default 'error'
                 (red), making a routine append confirmation look like a
                 failure. Same fix applied to the revision/new-quote
                 confirmation toast below. */
              showToast(importLabel + ' items added to ' + importTargetRef + '. Click Save when ready (or close the tab to discard).', 'success');
            }});
          }).catch(function(err) {
            /* CUSTOM_ROOMDESIGNER_BY_CLAUDE (Ryan 2026-06-12) — PENDING deals
               have no Shopify draft, so loadQuote/quote-details 404s. Load the
               pending record instead and fill it: customer prefills, items
               land, and the rep's Save converts the pending into the real
               quote on that same ref. */
            API.get('/pending-quote?ref=' + encodeURIComponent(importTargetRef)).then(function(data) {
              if (!data.pending) throw err;
              fillPendingQuote(importTargetRef, data.pending);
              applyImportedItems(importItems, { afterPricing: function() {
                state.suppressDirty = false; state.dirty = true;
                if (Q.updateSaveButton) Q.updateSaveButton();
                showToast(importLabel + ' items added to pending deal ' + importTargetRef + '. Click Save to create the quote (or close the tab to discard).', 'success');
              }});
            }).catch(function() {
              console.error('[WS Import] append: failed to load target', err);
              showToast('Could not load quote ' + importTargetRef + ' for append: ' + (err.message || 'unknown error'), 'error');
              // Fall back to dropping items into a fresh quote so work isn't lost
              applyImportedItems(importItems);
            });
          });
          });
        });
      } else if (importMode === 'existing' && importTargetRef) {
        /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-01) — ⛔ THE QUOTE ALREADY EXISTS AND IS
           ALREADY LOADING. The worker created it a moment ago and redirected here with `?ref=`, so
           this mode neither creates nor loads: it waits for the load in flight and then adds.

           ⚠️ NOT 'append', which re-POSTs /create-quote-from-lead — that would mint a SECOND
           Shopify draft inside the tag-search index lag — and calls loadQuote a second time,
           racing the one already running. */
        pendingExistingImport = function () {
          /* ⛔ THE STALE-KEY GUARD, which no other mode has and which this one needs. The key is
             global and survives navigation: click Create Quote on one deal, never arrive, then open
             another, and yesterday's rows would land on today's quote. Checked HERE rather than at
             parse time because `state.ref` is only trustworthy after the load. */
          if (String(state.ref || '') !== String(importTargetRef)) {
            console.warn('[WS Import] discarding a stale import for ' + importTargetRef + ' (this quote is ' + state.ref + ')');
            return;
          }
          if (!importStashedAt || (Date.now() - importStashedAt) > 300000) {
            console.warn('[WS Import] discarding an import older than five minutes');
            return;
          }

          /* The worker could not give us rows. Each status is a different sentence, and saying
             nothing is what made the original bug invisible for as long as it lasted. */
          var st = (importWdImport && importWdImport.status) || 'ok';
          if (st !== 'ok' || !importItems.length) {
            var msg;
            if (st === 'missing') {
              msg = 'Quote ' + importTargetRef + ' was created, but the customer\u2019s design link has '
                + 'expired, so the line items could not be brought over. Add them by hand, or ask the '
                + 'customer to resubmit.';
            } else if (st === 'no-order-data') {
              msg = 'Quote ' + importTargetRef + '\u2019s weight design has no recorded quantities. Open it '
                + 'from the deal\u2019s Files panel and hand it over from the designer to price it.';
            } else if (st === 'error') {
              msg = 'Quote ' + importTargetRef + ' was created, but the design could not be read ('
                + (importWdImport.reason || 'unknown error') + '). Add the line items by hand.';
            } else {
              msg = 'Quote ' + importTargetRef + ' was created, but the design carries no quantities to '
                + 'bring over. Add the line items by hand.';
            }
            showToast(msg, 'error', 9000);
            return;
          }

          /* ⛔ A DUP-GUARD, NOT `addItem`'s DUPLICATE DIALOG. Re-clicking Create Quote is the
             documented recovery when a rep loses the rows before saving, so landing here twice is
             expected — and a 35-rung ladder through the per-item dialog would be 35 modals. `sku`
             round-trips through save and reload, so this holds after a save too. */
          var have = {};
          (state.items || []).forEach(function (it) { if (it && it.sku) have[it.sku] = true; });
          var clash = importItems.filter(function (it) { return it && it.sku && have[it.sku]; });
          if (clash.length) {
            showToast(importTargetRef + ' already has the weight design\u2019s line items.', 'info', 6000);
            return;
          }

          if (importSkipped && importSkipped.length) {
            showToast(importSkipped.length + ' product(s) from the design are not in the quote catalogue '
              + 'and were skipped: ' + importSkipped.join(', ') + '. Add them by hand.', 'warning', 9000);
          }

          applyImportedItems(importItems, {
            afterPricing: function () {
              state.suppressDirty = false;
              state.dirty = true;
              if (Q.updateSaveButton) Q.updateSaveButton();
              showToast('Weight design items added to ' + importTargetRef
                + '. Review the quantities and click Save.', 'success', 7000);
            },
          });
        };
      } else if (importMode === 'revision' && importBaseRef) {
        /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — Full-screen overlay hides the -A flash,
           pricing batches, and Save As button flicker so the rep sees a clean
           "Creating revision..." state until -B is ready. Hidden on rq-ws:saved
           (success), validation-fail in afterPricing, the .catch error path, or
           a 30s safety timeout. */
        var revOverlayStyle = document.createElement('style');
        revOverlayStyle.textContent = '@keyframes rqWsRevSpin{to{transform:rotate(360deg)}}';
        document.head.appendChild(revOverlayStyle);
        var revOverlay = document.createElement('div');
        revOverlay.style.cssText = 'position:fixed;inset:0;background:#fff;z-index:99999;display:flex;flex-direction:column;align-items:center;justify-content:center;font-family:inherit;color:#000F1C;transition:opacity 0.2s;';
        // Honor dark mode of the underlying workstation
        if (wrap && wrap.classList && (wrap.classList.contains('rep-quote-dark') || document.body.classList.contains('rep-quote-dark'))) {
          revOverlay.style.background = '#0f1822';
          revOverlay.style.color = '#E5EDF5';
        }
        revOverlay.innerHTML = '<div style="width:48px;height:48px;border:3px solid rgba(127,127,127,0.25);border-top-color:#006BCC;border-radius:50%;animation:rqWsRevSpin 0.8s linear infinite;margin-bottom:18px;"></div><div style="font-size:16px;font-weight:600;letter-spacing:0.01em;">Creating new revision</div><div style="font-size:13px;opacity:0.7;margin-top:6px;">From ' + importBaseRef + ' &middot; copying contact &amp; terms</div>';
        document.body.appendChild(revOverlay);
        var revOverlayHidden = false;
        var hideRevOverlay = function() {
          if (revOverlayHidden) return;
          revOverlayHidden = true;
          document.removeEventListener('rq-ws:saved', hideRevOverlay);
          if (!revOverlay || !revOverlay.parentNode) return;
          revOverlay.style.opacity = '0';
          setTimeout(function() { if (revOverlay && revOverlay.parentNode) revOverlay.parentNode.removeChild(revOverlay); }, 220);
        };
        document.addEventListener('rq-ws:saved', hideRevOverlay);
        setTimeout(hideRevOverlay, 30000); // safety

        whenAuthed(function() {
          API.get('/rep-quotes?status=all&limit=200').then(function(data) {
            var quotes = data.quotes || [];
            var matching = quotes.filter(function(q) {
              var ref = q.reference || q.ref || '';
              var base = ref.replace(/-[A-Z]+$/, '');
              return base === importBaseRef && base !== ref;
            });
            if (!matching.length) throw new Error('No quotes found for chain ' + importBaseRef);
            // Pick the highest letter (longer letter strings beat shorter, e.g. 'AA' > 'Z')
            matching.sort(function(a, b) {
              var la = (((a.reference || a.ref || '').match(/-([A-Z]+)$/) || [])[1] || '');
              var lb = (((b.reference || b.ref || '').match(/-([A-Z]+)$/) || [])[1] || '');
              if (lb.length !== la.length) return lb.length - la.length;
              return lb.localeCompare(la);
            });
            var latestQ = matching[0];
            var latestRef = latestQ.reference || latestQ.ref;
            /* CUSTOM_ROOMDESIGNER_BY_CLAUDE (Ryan 2026-06-12) — when the
               chain's latest entry is a PENDING deal there is no quote to
               clone a new letter from. Fill the pending deal ITSELF with
               these items instead: the rep's Save converts it into the real
               quote on that ref, keeping the chain intact. */
            if (latestQ.isPending) {
              return API.get('/pending-quote?ref=' + encodeURIComponent(latestRef)).then(function(d) {
                if (!d.pending) throw new Error('Pending deal ' + latestRef + ' not found');
                hideRevOverlay();
                fillPendingQuote(latestRef, d.pending);
                applyImportedItems(importItems, { afterPricing: function() {
                  state.suppressDirty = false; state.dirty = true;
                  if (Q.updateSaveButton) Q.updateSaveButton();
                  showToast(importLabel + ' items added to pending deal ' + latestRef + '. Click Save to create the quote.', 'success');
                }});
                return '__pending_filled__';
              });
            }
            console.log('[WS Import] revision: latest in chain', importBaseRef, '=', latestRef);
            return Q.loadQuote(latestRef);
          }).then(function(_res) {
            if (_res === '__pending_filled__') return;
            // Compute the target revision letter immediately so the header bar
            // reflects -B from the moment items start populating, not after
            // pricing finishes. saveAsDraft (auto-clicked below) replaces it
            // with the real minted ref on success. Without this, the rep
            // briefly sees -A in the header while pricing batches finish.
            var match = (state.ref || '').match(/^(.+)-([A-Z]+)$/);
            var nextRef = '';
            if (match) {
              var letters = match[2];
              if (letters.length === 1 && letters !== 'Z') {
                nextRef = match[1] + '-' + String.fromCharCode(letters.charCodeAt(0) + 1);
              } else {
                nextRef = match[1] + '-' + letters + 'A';
              }
            }
            // pendingRevisionBase keeps the Save→SaveAs reroute active in
            // workstation-save.js as a safety net if auto-save below fails
            // validation (e.g. -A is missing a required field).
            state.pendingRevisionBase = state.ref || null;
            state.pendingRevisionNext = nextRef || null;
            if (dom.barRef && nextRef) dom.barRef.textContent = nextRef;

            // Replace source's items with the imported ones; saveAsDraft will
            // mint the next letter and write them into the new revision.
            state.items = [];

            // Reset quote-specific fields that shouldn't carry from the source revision
            // into the new one. Freight + install need to be re-quoted for the new item
            // mix; lead time may change. Tax rate, customer header, and Lead Source / CFA
            // DO carry (the tax amount auto-recomputes on the new total). Match the
            // empty-quote defaults from workstation-core.js (state initializer).
            state.fees[0] = { title: 'Shipping & Freight', amount: 0, status: 'pending' };
            state.fees[1] = { title: 'Installation', amount: 0, status: 'pending' };
            state.freight = {
              status: 'pending', requestedAt: null, requestedBy: null,
              vendors: [], blanketMarkup: false, blanketMarkupPct: 10,
              totalCost: 0, totalQuoted: 0, appliedAt: null, history: [], itemsSnapshot: [],
              deliveryConditions: { insideDelivery: false, loadingDock: false, forkLift: false, defaultDelivery: true }
            };
            state.installation = {
              status: 'pending', scope: '', requestedAt: null, requestedBy: null,
              companies: [], totalCost: 0, totalQuoted: 0, appliedAt: null, history: []
            };
            state.send.leadTime = '';

            // Sync the DOM — loadQuote populated these from the source; we need them
            // visibly reset to defaults before save-as fires and the user sees the result.
            ['shipping', 'installation'].forEach(function(fee) {
              var sel = wrap.querySelector('.rq-ws-fees__status[data-fee="' + fee + '"]');
              var input = wrap.querySelector('.rq-ws-fees__input[data-fee="' + fee + '"]');
              if (sel) { sel.value = 'pending'; syncCustomSelect(sel); }
              if (input) { input.value = ''; input.style.display = 'none'; }
            });
            var ltSel = wrap.querySelector('select[data-field="leadTime"]');
            if (ltSel) { ltSel.value = ''; syncCustomSelect(ltSel); }

            applyImportedItems(importItems, { afterPricing: function() {
              state.suppressDirty = false; state.dirty = true;
              if (Q.updateSaveButton) Q.updateSaveButton();
              // Auto-click Save As — mints -B via /clone-quote with customer +
              // lead source + CFA copied from -A by loadQuote. saveAsDraft
              // updates barRef to the real minted ref and clears
              // pendingRevisionBase on success. If validation fails (e.g. -A
              // missing required field), saveAsDraft shows an error toast and
              // the Save→SaveAs reroute lets the rep retry after fixing it.
              var saveAsBtn = wrap.querySelector('.rq-ws-send__save-as');
              if (saveAsBtn) {
                saveAsBtn.click();
                // Validation failed (e.g. -A missing required field) → button stayed
                // enabled → reveal the workstation so the rep can read the error toast
                // and fix the missing field. Save→SaveAs reroute will retry on click.
                if (!saveAsBtn.disabled) hideRevOverlay();
              } else {
                hideRevOverlay();
                /* Ryan 2026-05-21: 'success' kind → green toast. Same
                   reasoning as the append-mode toast above. */
                showToast(importLabel + ' loaded. Click Save to create ' + (nextRef || 'the next revision') + '.', 'success');
              }
            }});
          }).catch(function(err) {
            console.error('[WS Import] revision: failed', err);
            showToast('Could not create revision in ' + importBaseRef + ': ' + (err.message || 'unknown error'), 'error');
            hideRevOverlay();
            applyImportedItems(importItems);
          });
        });
      } else {
        // Unknown mode or missing required ref — fall back to fresh quote with items
        applyImportedItems(importItems);
      }
    } catch(e) { console.error('[WS Import] FATAL ERROR:', e.message, e.stack); }
  }

  /* ── Deferred render: if import added items before DOM was ready, render after auth ── */
  if (state.items.length > 0 && (!dom.itemsContainer || !dom.totals.msrp)) {
    document.addEventListener('rep-portal:authenticated', function() {
      // Re-query DOM elements that may have been null
      dom.totalsPanel = wrap.querySelector('.rq-ws-totals');
      dom.totals = {
        msrp: wrap.querySelector('.rq-ws-totals__msrp'),
        discount: wrap.querySelector('.rq-ws-totals__discount'),
        direct: wrap.querySelector('.rq-ws-totals__direct'),
        shipping: wrap.querySelector('.rq-ws-totals__shipping'),
        installation: wrap.querySelector('.rq-ws-totals__installation'),
        tax: wrap.querySelector('.rq-ws-totals__tax'),
        grand: wrap.querySelector('.rq-ws-totals__grand'),
        margin: wrap.querySelector('.rq-ws-totals__margin')
      };
      dom.itemsContainer = wrap.querySelector('.rq-ws-items');
      dom.itemsEmpty = wrap.querySelector('.rq-ws-items__empty');
      Q.renderItems();
    });
  }

  /* ── Auto-save recovery check ── */
  // Per-rep auto-save key prevents cross-user recovery on shared devices
  function getAutoSaveKey() {
    var sess = window.RepPortal && window.RepPortal.Auth.getSession();
    var code = sess ? (sess.code || '') : '';
    return 'rq_ws_autosave' + (code ? '_' + code : '');
  }
  var autoSaveKey = 'rq_ws_autosave'; // legacy fallback for cleanup
  var autoSaveRecoveryDismissed = false;
  function checkAutoSaveRecovery() {
    // Clean up legacy (non-rep-scoped) auto-save to prevent cross-user recovery
    localStorage.removeItem('rq_ws_autosave');
    var saved = localStorage.getItem(getAutoSaveKey());
    if (!saved) return;
    try {
      var data = JSON.parse(saved);
      // Only offer recovery if it's recent (< 4 hours) and has items
      var age = Date.now() - (data._savedAt || 0);
      if (age > 4 * 3600000 || !data.items || !data.items.length) {
        localStorage.removeItem(getAutoSaveKey());
        return;
      }
      /* CUSTOM_QWALERTS_BY_CLAUDE (Ryan 2026-10-23, Pass 2) - one row in the alerts banner. Restore is its button; the x is
         the old Dismiss (the saved copy is thrown away). */
      if (Q.showAlert) {
        var _age = Math.round(age / 60000);
        Q.showAlert({ id: 'autosave', level: 'yellow', icon: '&#x1F4BE;', persist: false,
          head: 'Unsaved quote recovered',
          text: (data.ref ? data.ref + ': ' : '') + data.items.length + ' item' + (data.items.length === 1 ? '' : 's') + ', saved ' + (_age < 60 ? _age + 'm' : Math.round(_age / 60) + 'h') + ' ago.',
          onDismiss: function () { localStorage.removeItem(getAutoSaveKey()); autoSaveRecoveryDismissed = true; },
          action: { label: 'Restore', run: function () {
            restoreAutoSave(data);
            localStorage.removeItem(getAutoSaveKey());
            Q.hideAlert('autosave');
          } } });
        return;
      }
      // Show recovery banner
      var banner = document.createElement('div');
      banner.style.cssText = 'background:#fef3c7;border:1px solid #fbbf24;border-radius:10px;padding:12px 16px;margin-bottom:16px;display:flex;align-items:center;gap:12px;font-size:14px;color:#92400e;';
      var refLabel = data.ref ? ' (' + data.ref + ')' : '';
      var itemCount = data.items.length;
      var timeAgo = Math.round(age / 60000);
      var timeLabel = timeAgo < 60 ? timeAgo + 'm ago' : Math.round(timeAgo / 60) + 'h ago';
      banner.innerHTML = '<span style="font-size:18px;">&#9888;</span>' +
        '<span style="flex:1;">Unsaved quote recovered' + refLabel + ' — ' + itemCount + ' items, saved ' + timeLabel + '</span>' +
        '<button type="button" style="padding:6px 16px;font-size:13px;font-weight:600;border:1px solid #f59e0b;border-radius:999px;background:#f59e0b;color:#fff;cursor:pointer;">Restore</button>' +
        '<button type="button" style="padding:6px 12px;font-size:13px;border:none;background:none;color:#92400e;cursor:pointer;text-decoration:underline;">Dismiss</button>';
      var container = wrap.querySelector('.rep-quote-container') || wrap;
      var header = wrap.querySelector('.rep-quote-header');
      if (header && header.nextSibling) container.insertBefore(banner, header.nextSibling);
      else container.prepend(banner);

      banner.querySelector('button:first-of-type').addEventListener('click', function() {
        restoreAutoSave(data);
        banner.remove();
        localStorage.removeItem(getAutoSaveKey());
      });
      banner.querySelector('button:last-of-type').addEventListener('click', function() {
        banner.remove();
        localStorage.removeItem(getAutoSaveKey());
        autoSaveRecoveryDismissed = true;
      });
    } catch (e) {
      localStorage.removeItem(getAutoSaveKey());
    }
  }

  function restoreAutoSave(data) {
    state.items = data.items || [];
    state.customFees = data.customFees || [];
    state.fees = data.fees || state.fees;
    state.tax = data.tax || state.tax;
    state.cardFee = data.cardFee || state.cardFee;
    state.freight = data.freight || state.freight;
    state.installation = data.installation || state.installation;
    state.send = data.send || state.send;
    state.leadSource = data.leadSource || '';
    state.cfaAttribution = data.cfaAttribution || '';
    // CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-09) — restore the manual repeat-buyer
    // override from the autosave snapshot; the pill refreshes after the customer renders.
    state.repeatBuyerOverride = !!data.repeatBuyerOverride;
    if (typeof Q.refreshRepeatPill === 'function') Q.refreshRepeatPill();
    state.referral = (data.referral && typeof data.referral === 'object')
      ? { name: String(data.referral.name || ''), gpPercent: parseFloat(data.referral.gpPercent) || 0 }
      : { name: '', gpPercent: 0 };
    state.referralSource = (data.referralSource && typeof data.referralSource === 'object')
      ? { name: String(data.referralSource.name || '') }
      : { name: '' };
    // CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-19) — migration (mirror of
    // loadQuote): a pre-split tracking referral sat in data.referral with gp 0
    // on a non-shared deal — move it to referralSource.
    if (!state.referralSource.name && state.referral.name && state.leadSource !== 'shared') {
      state.referralSource = { name: state.referral.name };
      state.referral = { name: '', gpPercent: 0 };
    }
    state.ref = data.ref || null;
    state.mode = data.ref ? 'edit' : 'new';
    state.suppressDirty = false; state.dirty = true;
    if (data.ref) {
      dom.barRef.textContent = data.ref;
          Q.updateBarCustomer();
      window.history.replaceState(null, '', window.location.pathname + '?ref=' + encodeURIComponent(data.ref));
    }
    // Restore customer fields
    if (data.customer) {
      Object.keys(data.customer).forEach(function(k) {
        var field = wrap.querySelector('[data-field="' + k + '"]');
        if (field && data.customer[k]) field.value = data.customer[k];
      });
    }
    // Restore dropdowns
    var lsSel = wrap.querySelector('select[data-field="leadSource"]');
    if (lsSel && data.leadSource) { lsSel.value = data.leadSource; syncCustomSelect(lsSel); }
    // Referral boxes — BLOCK A (shared recipient) + BLOCK B (referral source).
    // Fill inputs; defer show/hide to updateReferralBlockVisibility below.
    var refNameElB = wrap.querySelector('input[data-field="referralName"]');
    var refPctElB = wrap.querySelector('input[data-field="referralGpPercent"]');
    if (refNameElB) refNameElB.value = state.referral.name || '';
    if (refPctElB) refPctElB.value = state.referral.gpPercent ? state.referral.gpPercent : '';
    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (2026-07-24, DNI decoupling piece 3) — the Lead Source
       select + the Referred By input are gone; nothing to restore for them. state.referralSource
       is still carried (above) so a legacy value round-trips untouched through a save. */
    // Paint the Shared Deal recipient block now that Deal Type is restored.
    if (Q.updateReferralBlockVisibility) Q.updateReferralBlockVisibility();
    var cfaSel = wrap.querySelector('select[data-field="cfaAttribution"]');
    if (cfaSel && data.cfaAttribution) {
      cfaSel.value = data.cfaAttribution;
      syncCustomSelect(cfaSel);
    }
    // Re-apply disabled state if no CFA options
    if (cfaSel && cfaSel.disabled) {
      var cfaCw = wrap.querySelector('.rq-ws-custom-select[data-field="cfaAttribution"]');
      if (cfaCw && cfaCw._btn) cfaCw._btn.textContent = '—';
    }
    var termsSel = wrap.querySelector('select[data-field="terms"]');
    if (termsSel && data.send && data.send.terms) { termsSel.value = data.send.terms; syncCustomSelect(termsSel); }
    Q.renderItems();
    Q.recalcTotals();
  }

  /* ── Check for ?ref= on load ── */
  var params = new URLSearchParams(window.location.search);
  var fromParam = params.get('from');
  if (fromParam === 'dashboard') {
    var backBtn = document.getElementById('ws-back-btn');
    if (backBtn) { backBtn.href = '/pages/rep-quote-dashboard'; backBtn.innerHTML = '<span class="rep-quote-back__arrow">&larr;</span> Back to Dashboard'; }
  }
  var refParam = params.get('ref');
  var isPendingLoad = params.get('pending') === '1';
  if (!refParam) {
    // Only check auto-save recovery if not loading a specific quote
    checkAutoSaveRecovery();
  }
  if (refParam && !isPendingLoad) {
    // Pre-collapse customer section so it doesn't flash open then close on load
    var custBody = wrap.querySelector('.rq-ws-customer .rq-ws-section__body');
    var custIcon = wrap.querySelector('.rq-ws-customer .rq-ws-section__toggle-icon');
    if (custBody) { custBody.classList.remove('rq-ws-section__body--open'); custBody.style.maxHeight = '0'; custBody.style.overflow = 'hidden'; }
    if (custIcon) custIcon.textContent = '+';
  }
  if (refParam) {
    var refLoaded = false;
    function loadRefOnce() {
      if (refLoaded) return;
      refLoaded = true;
      if (isPendingLoad) {
        // Pre-fill from pending data, don't try to load Shopify draft order
        loadPendingQuote(refParam);
      } else {
        /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-01) — the 'existing' import runs AFTER this
           load, never beside it: `loadQuote` clears `state.items`, so rows added concurrently are
           wiped by whichever finishes second. Passed as both handlers because `loadQuote` swallows
           its own errors today and always resolves — a rejection would still mean the form is
           filled and the draft id is set, so applying rows is still the right move. */
        var _lq = Q.loadQuote(refParam);
        if (pendingExistingImport) {
          if (_lq && _lq.then) _lq.then(pendingExistingImport, pendingExistingImport);
          else pendingExistingImport();
        }
      }
    }
    // Wait for auth (fires after portal JS reveals content)
    document.addEventListener('rep-portal:authenticated', loadRefOnce);
    // If already authenticated (session in localStorage), load immediately
    if (Auth.getSession()) loadRefOnce();
  }

  function loadPendingQuote(ref) {
    API.get('/pending-quote?ref=' + encodeURIComponent(ref)).then(function(data) {
      if (!data.pending) return;
      fillPendingQuote(ref, data.pending);
    }).catch(function(err) {
      console.error('Load pending error:', err);
    });
  }

  // CUSTOM_ROOMDESIGNER_BY_CLAUDE (Ryan 2026-06-12) — fill logic split out of
  // loadPendingQuote so the designer-import paths (append/revision targeting
  // a PENDING deal) can reuse it with their own fetch + item flow.
  function fillPendingQuote(ref, p) {
      state.ref = ref;
      state.mode = 'new'; // Still "new" — no draft order exists yet
      dom.barRef.textContent = ref;
      Q.setStatusBadge('pending');
      /* CUSTOM_ROOMDESIGNER_BY_CLAUDE (ONYX a328b43a7755, 2026-10-13) — the SAME filler a real quote
         uses. This used to set the inputs itself, which never re-seeded the email chips (the Sold
         To email looked blank though it was there), never formatted the phone, and opened Ship To /
         Bill To only on the flags. populateCustomer does all three. */
      var cust = p.customer || {};
      Q.populateCustomer({ customer: cust });
      // Pre-fill notes
      var notesField = wrap.querySelector('[data-field="notes"]');
      if (notesField && p.note) notesField.value = p.note;
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-12; reworked 2026-06-19) — carry
         the pending's Terms & Options into the Workstation so nothing gets
         re-typed at quote time. Pendings now carry explicit Deal Type / CFA /
         payment terms etc. when edited via the DD "Edit Deal Terms" popup; older
         +New-Pending records carry only a referral + channel. Prefer explicit
         fields, fall back to the GP% heuristic for legacy records. */
      var lsSelP = wrap.querySelector('select[data-field="leadSource"]');
      var pGp = (p.referral && p.referral.name) ? (parseFloat(p.referral.gpPercent) || 0) : 0;

      // Deal Type: explicit p.leadSource wins; else infer from a paying referral
      // (GP% > 0 → shared); else a named referral → inbound (tracking-ish).
      var dealType = (p.leadSource && ['inbound', 'outbound', 'shared'].indexOf(p.leadSource) !== -1) ? p.leadSource : '';
      if (!dealType && p.referral && p.referral.name) dealType = pGp > 0 ? 'shared' : 'inbound';
      if (dealType) { state.leadSource = dealType; if (lsSelP) { lsSelP.value = dealType; syncCustomSelect(lsSelP); } }

      /* CUSTOM_MARKETINGOPS_BY_CLAUDE (2026-07-24, DNI decoupling piece 3) — the Lead Source
         channel prefill is gone with the dropdown. A pending's captured leadContext still rides
         onto the quote server-side (handleSaveWorkstation), where Marketing Ops reads it. */

      // BLOCK A — Shared Deal recipient (paid). From p.referral on a Shared Deal.
      if (dealType === 'shared' && p.referral && p.referral.name) {
        state.referral = { name: String(p.referral.name), gpPercent: pGp };
        var refNameP = wrap.querySelector('input[data-field="referralName"]');
        var refPctP = wrap.querySelector('input[data-field="referralGpPercent"]');
        if (refNameP) refNameP.value = state.referral.name;
        if (refPctP) refPctP.value = pGp ? pGp : '';
      }
      // BLOCK B ("Referred By") is retired as an input — a pending's legacy referral note is
      // carried in state only, so an existing value survives the save untouched.
      var srcName = (p.referralSource && p.referralSource.name) ? String(p.referralSource.name)
        : ((p.referral && p.referral.name && dealType !== 'shared') ? String(p.referral.name) : '');
      if (srcName) state.referralSource = { name: srcName };

      // CFA attribution (DD popup). The async /cfa-list loader re-applies this.
      if (p.cfaAttribution) {
        state.cfaAttribution = p.cfaAttribution;
        var cfaSelP = wrap.querySelector('select[data-field="cfaAttribution"]');
        if (cfaSelP) { cfaSelP.value = p.cfaAttribution; syncCustomSelect(cfaSelP); }
      }
      // Payment terms / lead time / expiration (DD popup).
      if (p.send && typeof p.send === 'object') {
        state.send = state.send || {};
        if (p.send.terms) { state.send.terms = p.send.terms; var tSelP = wrap.querySelector('select[data-field="terms"]'); if (tSelP) { tSelP.value = p.send.terms; syncCustomSelect(tSelP); } }
        if (p.send.leadTime) { state.send.leadTime = p.send.leadTime; var ltSelP = wrap.querySelector('select[data-field="leadTime"]'); if (ltSelP) { ltSelP.value = p.send.leadTime; syncCustomSelect(ltSelP); } }
        if (p.send.expiration) { state.send.expiration = p.send.expiration; var expElP = wrap.querySelector('[data-field="expiration"]'); if (expElP) expElP.value = p.send.expiration; }
      }

      if (Q.syncInquirySourceState) Q.syncInquirySourceState();
      // Paint both referral boxes after the selects resolve.
      if (Q.updateReferralBlockVisibility) Q.updateReferralBlockVisibility();
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      // Remove ?pending=1 from URL so save uses the ref normally
      if (window.history.replaceState) {
        window.history.replaceState(null, '', window.location.pathname + '?ref=' + encodeURIComponent(ref));
      }
  }

  /* ── Set default expiration ── */
  wrap.querySelector('[data-field="expiration"]').value = state.send.expiration;

  /* ── Sub-description blur: clean up empties when focus leaves the area ── */
  var subdescBlurTimer = null;
  dom.itemsContainer.addEventListener('focusout', function(e) {
    var area = e.target.closest('.rq-ws-subdesc-area');
    if (!area) return;
    var itemId = area.getAttribute('data-item-id');
    if (!itemId) return;

    clearTimeout(subdescBlurTimer);
    subdescBlurTimer = setTimeout(function() {
      // Bail if area was reopened or focus returned
      if (!area.classList.contains('rq-ws-subdesc-area--open')) return;
      if (area.contains(document.activeElement)) return;

      var item = findItem(itemId);
      if (!item || !item.subDescriptions) return;

      // Remove all empty sub-descriptions (iterate backwards by row position)
      var rows = area.querySelectorAll('.rq-ws-subdesc-row');
      for (var i = rows.length - 1; i >= 0; i--) {
        var inp = rows[i].querySelector('input');
        if (inp && i < item.subDescriptions.length && item.subDescriptions[i] === '') {
          item.subDescriptions.splice(i, 1);
          rows[i].remove();
        }
      }

      // If none left, collapse
      if (!item.subDescriptions.length) {
        area.classList.remove('rq-ws-subdesc-area--open');
        var toggle = dom.itemsContainer.querySelector('.rq-ws-subdesc-toggle[data-item-id="' + itemId + '"]');
        if (toggle) { toggle.classList.remove('rq-ws-subdesc-toggle--open'); toggle.textContent = '⋯'; }
      }
    }, 250);
  });

  /* ── Reusable Input Prompt ── */
  var inputPromptEl = wrap.querySelector('.rq-ws-input-prompt');
  var inputPromptInput = inputPromptEl.querySelector('.rq-ws-input-prompt__input');
  var inputPromptCallback = null;

  function showInputPrompt(title, label, opts) {
    opts = opts || {};
    inputPromptEl.querySelector('.rq-ws-input-prompt__title').textContent = title;
    inputPromptEl.querySelector('.rq-ws-input-prompt__label').textContent = label;
    inputPromptEl.querySelector('.rq-ws-input-prompt__confirm').textContent = opts.btnText || 'Apply';
    inputPromptInput.value = opts.defaultValue || '';
    inputPromptInput.type = opts.inputType || 'text';
    inputPromptInput.placeholder = opts.placeholder || '';
    inputPromptEl.style.display = '';
    setTimeout(function() { inputPromptInput.focus(); inputPromptInput.select(); }, 50);

    return new Promise(function(resolve) {
      inputPromptCallback = resolve;
    });
  }

  function closeInputPrompt(value) {
    inputPromptEl.style.display = 'none';
    if (inputPromptCallback) {
      var cb = inputPromptCallback;
      inputPromptCallback = null;
      cb(value);
    }
  }

  inputPromptEl.querySelector('.rq-ws-input-prompt__confirm').addEventListener('click', function() {
    closeInputPrompt(inputPromptInput.value);
  });
  inputPromptEl.querySelector('.rq-ws-input-prompt__cancel').addEventListener('click', function() {
    closeInputPrompt(null);
  });
  inputPromptEl.querySelector('.rq-ws-input-prompt__backdrop').addEventListener('click', function() {
    closeInputPrompt(null);
  });
  inputPromptInput.addEventListener('keydown', function(e) {
    if (e.key === 'Enter') { e.preventDefault(); closeInputPrompt(inputPromptInput.value); }
    if (e.key === 'Escape') { closeInputPrompt(null); }
  });

  /* ── Weight Series: SKU Pattern Detection ── */
  var skuPropTimer = null;
  function propagateSeriesSku(sourceItem, val) {
    clearTimeout(skuPropTimer);
    skuPropTimer = setTimeout(function() {
      if (!val) {
        // Clear all siblings
        state.items.forEach(function(si) {
          if (si.seriesId !== sourceItem.seriesId || si.id === sourceItem.id) return;
          si.sku = '';
          var inp = dom.itemsContainer.querySelector('tr[data-item-id="' + si.id + '"] [data-field="seriesSku"]');
          if (inp) inp.value = '';
        });
        return;
      }

      // Find the weight number in the SKU string
      var w = sourceItem.seriesWeight;
      var match = findWeightInSku(val, w);
      if (!match) return; // no recognizable weight number — don't propagate

      // For each sibling, replace the matched portion with their weight in same format
      state.items.forEach(function(si) {
        if (si.seriesId !== sourceItem.seriesId || si.id === sourceItem.id) return;
        var replacement = formatWeight(si.seriesWeight, match.padding);
        si.sku = val.substring(0, match.start) + replacement + val.substring(match.end);
        var inp = dom.itemsContainer.querySelector('tr[data-item-id="' + si.id + '"] [data-field="seriesSku"]');
        if (inp) inp.value = si.sku;
      });
    }, 200);
  }

  function findWeightInSku(sku, weight) {
    // Try to find the weight number in the SKU, checking most-padded formats first
    // For weight 5: try "005", "05", "5"
    // For weight 45: try "045", "45"
    // For weight 100: try "100"
    // For weight 2.5: try "2.5", "2_5"
    var candidates = [];
    var n = parseFloat(weight);
    if (n !== Math.floor(n)) {
      // Decimal weight — try as-is and underscore variant
      candidates.push(String(weight));
      candidates.push(String(weight).replace('.', '_'));
    } else {
      // Integer — try zero-padded from widest to narrowest
      if (n < 10) candidates.push('00' + n);
      if (n < 100) candidates.push(('0' + n).slice(-2));
      candidates.push(String(n));
    }

    for (var i = 0; i < candidates.length; i++) {
      var c = candidates[i];
      // Search from the end (weight is usually a suffix)
      var idx = sku.lastIndexOf(c);
      if (idx !== -1) {
        // Make sure we're not matching inside a longer number
        var before = idx > 0 ? sku[idx - 1] : '';
        var after = idx + c.length < sku.length ? sku[idx + c.length] : '';
        var beforeIsDigit = /\d/.test(before);
        var afterIsDigit = /\d/.test(after);
        // Allow match if not surrounded by other digits (unless the candidate itself covers the full number)
        if (!beforeIsDigit && !afterIsDigit) {
          return { start: idx, end: idx + c.length, padding: c.length };
        }
      }
    }
    return null;
  }

  function formatWeight(weight, padding) {
    var n = parseFloat(weight);
    if (n !== Math.floor(n)) {
      // Decimal weight
      return String(weight);
    }
    var s = String(n);
    while (s.length < padding) s = '0' + s;
    return s;
  }

  /* ── Weight Series: Utilities ── */
  /* `zeroPad` moved up beside `wdSkuSuffix` (2026-09-08) so `seriesRungFrom` can use it. */

  /* ── Weight Series: Modal Logic ── */
  var weightModal = wrap.querySelector('.rq-ws-weight-modal');
  var weightPillsContainer = weightModal.querySelector('.rq-ws-weight-modal__pills');
  var weightSummary = weightModal.querySelector('.rq-ws-weight-modal__summary');
  var weightModalState = {
    weights: [],       // all available weights
    selected: [],      // currently selected
    disabled: [],      // existing weights (when adding to series)
    existingSeriesId: null,
    isPlates: false,   // true if Plates preset was used
    /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (2026-09-08) — the house-brand line picked for a NEW series:
       `{key, entry}` from WD_CATALOG, or null. Add Series then builds the rows through wdRowsFor —
       the designer handoff's own builder — so a series a rep starts by hand is the same rows a
       customer's design would have produced: brand heading, real SKUs, priced, noted. */
    ballast: null,
    ladderSeq: 0       // guards a slow ladder fetch against a newer pick or a closed modal
  };
  var ballastSelect = weightModal.querySelector('[data-ballast-select]');

  function openSeriesModal(existingSeriesId) {
    weightModalState.weights = [];
    weightModalState.selected = [];
    weightModalState.disabled = [];
    weightModalState.existingSeriesId = existingSeriesId || null;
    weightModalState.isPlates = false;
    weightModalState.ballast = null;
    weightModalState.ladderSeq++;
    if (ballastSelect) { ballastSelect.value = ''; ballastSelect.removeAttribute('data-loading'); }

    // If adding to existing series, pre-populate disabled weights
    var catalogueSibling = null;
    if (existingSeriesId) {
      state.items.forEach(function(item) {
        if (item.seriesId === existingSeriesId) {
          weightModalState.disabled.push(item.seriesWeight);
          if (weightModalState.weights.indexOf(item.seriesWeight) === -1) {
            weightModalState.weights.push(item.seriesWeight);
          }
          if (item.productHandle || item._productHandle) catalogueSibling = item;
        }
      });
    }
    /* A catalogue-backed series (the handoff's, or one built from the house-brand pick) offers its
       product's REAL ladder — the 2.5 lb rungs and the 150 lb top the Dumbbells preset does not
       know about. Nothing is pre-selected; the rep ticks what the customer wants. The pick control
       is hidden here: the line is already decided by the series being added to. */
    if (ballastSelect) ballastSelect.style.display = existingSeriesId ? 'none' : '';
    if (catalogueSibling) {
      var handle = catalogueSibling.productHandle || catalogueSibling._productHandle;
      var line = catalogueSibling.seriesBaseSku || '';
      loadLadderInto({ line: line, title: catalogueSibling.seriesBaseTitle || line }, handle, { select: false });
    }

    weightModal.style.display = '';
    renderWeightPills();
    updateWeightSummary();

    // Reset quick fill inputs
    weightModal.querySelectorAll('.rq-ws-weight-modal__qf-input').forEach(function(el) {
      if (el.getAttribute('data-qf') !== 'step') el.value = '';
    });
    weightModal.querySelector('.rq-ws-weight-modal__manual-input').value = '';

    // Update modal title
    var titleEl = weightModal.querySelector('.rq-ws-weight-modal__title');
    titleEl.textContent = existingSeriesId ? 'Add to Weight Series' : 'Weight Series';
    var addBtn = weightModal.querySelector('.rq-ws-weight-modal__add');
    addBtn.textContent = existingSeriesId ? 'Add Weights' : 'Add Series';
  }

  function closeWeightModal() {
    weightModal.style.display = 'none';
  }

  var weightPillsSection = weightModal.querySelector('.rq-ws-weight-modal__pills-section');
  var weightManualSection = weightModal.querySelector('.rq-ws-weight-modal__manual');

  function renderWeightPills() {
    var sorted = weightModalState.weights.slice().sort(function(a, b) { return a - b; });
    var hasWeights = sorted.length > 0;
    weightPillsSection.classList.toggle('rq-ws-weight-modal__pills-section--visible', hasWeights);
    weightManualSection.classList.toggle('rq-ws-weight-modal__manual--visible', hasWeights);
    var html = '';
    sorted.forEach(function(w) {
      var isDisabled = weightModalState.disabled.indexOf(w) !== -1;
      var isSelected = weightModalState.selected.indexOf(w) !== -1;
      var cls = 'rq-ws-weight-pill';
      if (isDisabled) cls += ' rq-ws-weight-pill--disabled';
      else if (isSelected) cls += ' rq-ws-weight-pill--selected';
      html += '<button class="' + cls + '" data-weight="' + w + '" type="button">' + w + ' lbs</button>';
    });
    weightPillsContainer.innerHTML = html;
  }

  function updateWeightSummary() {
    var count = weightModalState.selected.length;
    weightSummary.textContent = count > 0 ? '(' + count + ' selected)' : '';
  }

  // Pill click
  weightPillsContainer.addEventListener('click', function(e) {
    var pill = e.target.closest('.rq-ws-weight-pill');
    if (!pill || pill.classList.contains('rq-ws-weight-pill--disabled')) return;
    var w = parseFloat(pill.getAttribute('data-weight'));
    var idx = weightModalState.selected.indexOf(w);
    if (idx === -1) weightModalState.selected.push(w);
    else weightModalState.selected.splice(idx, 1);
    renderWeightPills();
    updateWeightSummary();
  });

  // Preset buttons
  var presets = {
    dumbbells: { start: 5, end: 100, step: 5 },
    plates: { weights: [2.5, 5, 10, 15, 25, 35, 45] }
  };
  weightModal.querySelectorAll('.rq-ws-weight-modal__preset').forEach(function(btn) {
    btn.addEventListener('click', function() {
      var key = this.getAttribute('data-preset');
      var p = presets[key];
      if (!p) return;
      if (key === 'plates') weightModalState.isPlates = true;
      var list = p.weights || [];
      if (p.start != null) {
        for (var w = p.start; w <= p.end + 0.001; w += p.step) list.push(Math.round(w * 100) / 100);
      }
      list.forEach(function(w) {
        if (weightModalState.weights.indexOf(w) === -1) weightModalState.weights.push(w);
        if (weightModalState.disabled.indexOf(w) === -1 && weightModalState.selected.indexOf(w) === -1) weightModalState.selected.push(w);
      });
      renderWeightPills();
      updateWeightSummary();
    });
  });

  /* ── The house-brand pick (CUSTOM_WEIGHTDESIGNER_BY_CLAUDE, Ryan 2026-09-08) ──
     RW-1387-A's customer asked for a rubber dumbbell quote alongside her urethane one. The
     Workstation's product search only returns ACTIVE products and the Ballast lines are drafts,
     so the only way to quote one by hand was to type every title, SKU, MAP and cost off a product
     page the rep cannot easily see. The options come from WD_CATALOG — the same map the designer
     handoff resolves rows through — so the brand's lines are one list in one file. */
  if (ballastSelect) {
    wdPickableLines().forEach(function(p) {
      var opt = document.createElement('option');
      opt.value = p.key;
      opt.textContent = WD_VENDOR + ' ' + p.label;
      ballastSelect.appendChild(opt);
    });
    ballastSelect.addEventListener('change', function() {
      var key = ballastSelect.value;
      weightModalState.ballast = null;
      if (!key || !WD_CATALOG[key]) { weightModalState.ladderSeq++; return; }
      var entry = WD_CATALOG[key];
      weightModalState.ballast = { key: key, entry: entry };
      weightModalState.isPlates = entry.line !== 'BS-RRD' && entry.line !== 'BS-RUD';
      /* A fresh pick REPLACES whatever a preset put in the pills: the product's ladder is the
         truth for this line, and a 55 lb rung a dumbbell preset offered does not exist on a
         plate. Rungs already on the series (disabled) are never removed. */
      weightModalState.weights = weightModalState.disabled.slice();
      weightModalState.selected = [];
      renderWeightPills();
      updateWeightSummary();
      loadLadderInto(entry, entry.handle, { select: true });
    });
  }

  /* Fetch a line's ladder and put it in the pills. `select:true` ticks every rung not already on
     the series (a new series: the rep un-ticks what the customer does not want); `select:false`
     only offers them. A stale response (a newer pick, or the modal closed and reopened) is
     dropped on `ladderSeq`. A failed read is SAID — the pills would otherwise just stay empty,
     which reads as "this product has no weights". */
  function loadLadderInto(entry, handle, opts) {
    if (!handle) return;
    var seq = ++weightModalState.ladderSeq;
    if (ballastSelect) ballastSelect.setAttribute('data-loading', '1');
    API.get('/product-by-handle?handle=' + encodeURIComponent(handle)).then(function(data) {
      if (seq !== weightModalState.ladderSeq) return;
      var ladder = wdLadderFrom(entry, data && data.product);
      if (!ladder.length) throw new Error('no weights found on ' + handle);
      ladder.forEach(function(w) {
        if (weightModalState.weights.indexOf(w) === -1) weightModalState.weights.push(w);
        if (opts && opts.select && weightModalState.disabled.indexOf(w) === -1 && weightModalState.selected.indexOf(w) === -1) weightModalState.selected.push(w);
      });
      renderWeightPills();
      updateWeightSummary();
    }).catch(function(err) {
      if (seq !== weightModalState.ladderSeq) return;
      showToast('Could not read the ' + ((entry && entry.title) || handle) + ' weight ladder: '
        + ((err && err.message) || 'unknown error') + '. Use a preset or type the weights.', 'error', 8000);
    }).then(function() {
      if (ballastSelect && seq === weightModalState.ladderSeq) ballastSelect.removeAttribute('data-loading');
    });
  }

  // Quick Fill Generate
  weightModal.querySelector('.rq-ws-weight-modal__qf-btn').addEventListener('click', function() {
    var start = parseFloat(weightModal.querySelector('[data-qf="start"]').value);
    var end = parseFloat(weightModal.querySelector('[data-qf="end"]').value);
    var step = parseFloat(weightModal.querySelector('[data-qf="step"]').value);
    if (isNaN(start) || isNaN(end) || isNaN(step) || step <= 0 || start > end) return;

    for (var w = start; w <= end + 0.001; w += step) {
      var rounded = Math.round(w * 100) / 100;
      if (weightModalState.weights.indexOf(rounded) === -1) {
        weightModalState.weights.push(rounded);
      }
      if (weightModalState.disabled.indexOf(rounded) === -1 && weightModalState.selected.indexOf(rounded) === -1) {
        weightModalState.selected.push(rounded);
      }
    }
    renderWeightPills();
    updateWeightSummary();
  });

  // Manual Add
  function addManualWeight() {
    var input = weightModal.querySelector('.rq-ws-weight-modal__manual-input');
    var w = parseFloat(input.value);
    if (isNaN(w) || w <= 0) return;
    w = Math.round(w * 100) / 100;
    if (weightModalState.weights.indexOf(w) === -1) {
      weightModalState.weights.push(w);
    }
    if (weightModalState.disabled.indexOf(w) === -1 && weightModalState.selected.indexOf(w) === -1) {
      weightModalState.selected.push(w);
    }
    input.value = '';
    renderWeightPills();
    updateWeightSummary();
  }
  weightModal.querySelector('.rq-ws-weight-modal__manual-btn').addEventListener('click', addManualWeight);
  weightModal.querySelector('.rq-ws-weight-modal__manual-input').addEventListener('keydown', function(e) {
    if (e.key === 'Enter') { e.preventDefault(); addManualWeight(); }
  });

  // Cancel / Close
  weightModal.querySelector('.rq-ws-weight-modal__cancel').addEventListener('click', closeWeightModal);
  weightModal.querySelector('.rq-ws-weight-modal__close').addEventListener('click', closeWeightModal);
  weightModal.querySelector('.rq-ws-weight-modal__backdrop').addEventListener('click', closeWeightModal);

  // Add Series / Add Weights
  weightModal.querySelector('.rq-ws-weight-modal__add').addEventListener('click', function() {
    if (!weightModalState.selected.length) return;

    /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-08) — a NEW series from the house-brand pick
       is built by `wdRowsFor`, the designer handoff's own builder, from the picked weights at
       quantity 0: the brand heading, the real SKUs, the "Pair" in a dumbbell's name, the "Custom
       design included in price" note on the top rung, the -1 MSRP sentinel. Rows go through
       Q.addItem like the import's do, then the shared back-fill prices them. No vendor prompt —
       the vendor is the whole point of the pick. */
    if (!weightModalState.existingSeriesId && weightModalState.ballast) {
      var pick = weightModalState.ballast;
      var picked = weightModalState.selected.slice().sort(function(a, b) { return a - b; });
      var built = wdRowsFor([{ product: pick.key, unit: pick.entry.unit, configuration: null,
        lines: picked.map(function(w) { return { weight: w, qty: 0 }; }) }]);
      var needs = [];
      built.rows.forEach(function(row) {
        Q.addItem(Object.assign({}, row, { _skipRender: true, _skipDupCheck: true }));
        var added = state.items[state.items.length - 1];
        if (row._productHandle && added) needs.push({ handle: row._productHandle, sku: row.sku, itemId: added.id });
      });
      closeWeightModal();
      Q.renderItems();
      validateAllRows();
      if (needs.length) backfillPricing(needs);
      return;
    }

    var seriesId = weightModalState.existingSeriesId || ('series_' + Math.random().toString(36).substr(2, 9));

    /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-08, RW-1387-A) — adding to an EXISTING series
       builds every rung from a sibling (`seriesRungFrom`): same vendor, so it lands under the same
       heading instead of a new "CUSTOM" group; same product handle and the real SKU shape, so the
       pricing back-fill can fill MAP and cost the way the handoff's own rows were filled. The
       sibling is the series' HIGHEST rung — the one carrying the sub-descriptions and the one a
       later rung most resembles. */
    var sibling = null;
    if (weightModalState.existingSeriesId) {
      state.items.forEach(function(i) {
        if (i.seriesId !== seriesId) return;
        if (!sibling || (Number(i.seriesWeight) || 0) > (Number(sibling.seriesWeight) || 0)) sibling = i;
      });
    }

    var sorted = weightModalState.selected.slice().sort(function(a, b) { return a - b; });

    // Find insertion index (after last existing series item, or at end)
    var insertIdx = state.items.length;
    if (weightModalState.existingSeriesId) {
      for (var i = state.items.length - 1; i >= 0; i--) {
        if (state.items[i].seriesId === seriesId) { insertIdx = i + 1; break; }
      }
    }

    var needsPricing = [];
    sorted.forEach(function(w, wi) {
      var newItem;
      if (sibling) {
        newItem = seriesRungFrom(sibling, w, seriesId);
        newItem.id = genId();
        if (newItem.productHandle) needsPricing.push({ handle: newItem.productHandle, sku: newItem.sku, itemId: newItem.id });
      } else {
        /* A brand-new series: nothing to inherit. The vendor prompt below names the group. */
        newItem = {
          id: genId(),
          type: 'custom',
          variantId: null,
          vendor: 'Custom',
          title: '',
          sku: '',
          quantity: 0,
          msrp: 0,
          cost: 0,
          points: 30,
          directPrice: 0,
          mapPrice: 0,
          isFree: false,
          subDescriptions: [],
          internalNote: '',
          seriesId: seriesId,
          seriesWeight: w,
          seriesBaseTitle: null,
          seriesBaseSku: null,
          seriesIsPlates: weightModalState.isPlates || false
        };
      }
      state.items.splice(insertIdx + wi, 0, newItem);
    });

    // Move sub-descriptions to the new last item in the series (highest weight)
    var seriesItems = state.items.filter(function(i) { return i.seriesId === seriesId; });
    seriesItems.sort(function(a, b) { return (a.seriesWeight || 0) - (b.seriesWeight || 0); });
    var newLast = seriesItems[seriesItems.length - 1];
    if (newLast) {
      seriesItems.forEach(function(si) {
        if (si.id === newLast.id) return;
        if (si.subDescriptions && si.subDescriptions.length) {
          // Move subs to the new last item
          newLast.subDescriptions = (newLast.subDescriptions || []).concat(si.subDescriptions);
          si.subDescriptions = [];
        }
      });
    }

    closeWeightModal();
    Q.renderItems();
    validateAllRows();
    /* Price the added rungs from the product's own variants — MAP, cost, points, variantId — the
       same call the handoff's rows go through. A rung the catalogue cannot match is named in a
       toast rather than left silently at $0. */
    if (needsPricing.length) backfillPricing(needsPricing);

    // For new series, prompt for vendor name
    if (!weightModalState.existingSeriesId) {
      var newIds = [];
      state.items.forEach(function(i) { if (i.seriesId === seriesId) newIds.push(i.id); });
      openVendorPrompt(newIds);
    }
  });

  // Weight Series button in custom dropdown
  var customWrap = wrap.querySelector('.rq-ws-custom-add-wrap');
  customWrap.querySelector('[data-action="weight-series"]').addEventListener('click', function() {
    customWrap.classList.remove('rq-ws-custom-add-wrap--open');
    // Flush any pending custom items first
    var pendingCustomIds = Q.pendingCustomIds || [];
    if (pendingCustomIds.length) {
      var ids = pendingCustomIds.slice();
      Q.pendingCustomIds = [];
      Q.vendorPromptCallback = function() { openSeriesModal(null); };
      openVendorPrompt(ids);
    } else {
      openSeriesModal(null);
    }
  });

  // "+ weight" button click on last series item (delegated)
  dom.itemsContainer.addEventListener('click', function(e) {
    var addBtn = e.target.closest('.rq-ws-series-add-weight');
    if (addBtn) {
      var seriesId = addBtn.getAttribute('data-series-id');
      openSeriesModal(seriesId);
    }
  });

  /* ── Vendor Prompt ── */
  var vendorPrompt = wrap.querySelector('.rq-ws-vendor-prompt');
  var vendorInput = vendorPrompt.querySelector('.rq-ws-vendor-prompt__input');
  var vendorDropdown = vendorPrompt.querySelector('.rq-ws-vendor-prompt__dropdown');
  var vendorTargetIds = [];
  var vendorActiveIdx = -1;
  var vendorPromptCallback = null;

  // All vendors from the Shopify store (rendered by Liquid at build time) + hardcoded extras
  var siteVendors = (window.__shopVendors || []).slice();
  ['Blue Sky Fitness Supply', 'BSFS'].forEach(function(v) {
    if (siteVendors.indexOf(v) === -1) siteVendors.push(v);
  });
  siteVendors.sort();

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-01) — ⛔ ALIASES AS LABELS, NEVER AS IDENTITIES.
     Ryan asked for "Ballast Strength" and "Ballast Strength by Blue Sky Fitness Supply" to both be
     pickable. They are two ways of WRITING one supplier, not two suppliers, and the difference is
     load-bearing: grouping compares the vendor string EXACTLY (`canonicalVendor`) while PO
     membership compares it FUZZILY (`vendorMatches`). Store a byline as `item.vendor` and you get
     two Purchasing rows whose POs each match ALL the lines — the factory receives two purchase
     orders covering the whole order, with nothing on screen saying so. So the alias travels as a
     LABEL and `applyVendor` folds it back to the canonical name before anything else sees it.

     ⚠ SINGLE-CLAIMANT ONLY. An alias two vendors claim is the Vendor Guide REFUSING to choose
     (`/vendor-by-name` answers `ambiguous` and asks a human). Offering it here and silently
     resolving it would manufacture exactly the guess that design exists to prevent. */
  var wsAliasLabels = [];          // display strings, in the order the picker should offer them
  var wsAliasToVendor = {};        // lowercased label -> canonical vendor name
  var wsAliasesLoaded = false;
  function loadVendorAliases() {
    if (wsAliasesLoaded) return;
    wsAliasesLoaded = true;        // set FIRST: a failed fetch must not retry on every keystroke
    API.get('/vendor-workflows').then(function(data) {
      var claimants = (data && data.aliases) || {};
      var labels = (data && data.aliasLabels) || {};
      Object.keys(claimants).forEach(function(k) {
        var owners = claimants[k];
        if (!Array.isArray(owners) || owners.length !== 1) return;
        var canonical = owners[0];
        /* ⚠ OFFER THE VENDOR ITSELF, not only its aliases. `shop.vendors` is derived from
           PUBLISHED products, so a supplier whose catalogue is still DRAFT is invisible to the
           picker even though the Guide knows it perfectly well — which is exactly the Ballast
           case. A vendor that has a quote alias is by definition one we sell under our own name,
           so it belongs in the list whether or not its products are live yet. */
        if (wsAliasLabels.indexOf(canonical) === -1) wsAliasLabels.push(canonical);
        /* ⛔ NEVER OFFER THE KEY AS A LABEL. `aliases` is keyed LOWERCASED because it is a
           MATCHING map; `aliasLabels` is the same set with the casing a person typed. Falling back
           to the key put "ballast strength by blue sky fitness supply" in front of a rep — and,
           because `Array.sort()` is ASCII and every uppercase letter sorts before every lowercase
           one, dumped both entries at the BOTTOM of an otherwise alphabetical list. One bug, two
           symptoms. Skipping is the honest fallback: an un-cased byline is worse than no byline,
           and the option reappears by itself once the Guide worker serves `aliasLabels`. */
        var label = labels[k];
        if (!label || label === canonical) return;
        wsAliasToVendor[label.toLowerCase()] = canonical;
        if (wsAliasLabels.indexOf(label) === -1) wsAliasLabels.push(label);
      });
    }).catch(function(err) {
      console.warn('[WS] vendor aliases unavailable, picker shows catalog vendors only:', err && err.message);
    });
  }

  function getKnownVendors() {
    var seen = {};
    var vendors = [];
    siteVendors.forEach(function(v) {
      if (v && !seen[v]) { seen[v] = true; vendors.push(v); }
    });
    // Also include any custom vendors from current quote
    state.items.forEach(function(i) {
      var v = i.vendor;
      if (v && v !== 'Custom' && !seen[v]) { seen[v] = true; vendors.push(v); }
    });
    wsAliasLabels.forEach(function(a) {
      if (!seen[a]) { seen[a] = true; vendors.push(a); }
    });
    /* ⚠ CASE-INSENSITIVE. The default sort is ASCII, so "BSFS" precedes "Blue Sky" and anything
       lowercase lands after everything uppercase. A rep reads this list alphabetically or not at
       all. */
    vendors.sort(function(a, b) {
      return String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
    });
    return vendors;
  }

  var vendorQuickWrap = vendorPrompt.querySelector('.rq-ws-vendor-prompt__quick');
  var vendorQuickPills = vendorPrompt.querySelector('.rq-ws-vendor-prompt__quick-pills');
  var vendorInsertWrap = vendorPrompt.querySelector('.rq-ws-vendor-prompt__insert');
  var vendorInsertList = vendorPrompt.querySelector('.rq-ws-vendor-prompt__insert-list');

  function openVendorPrompt(itemIds) {
    loadVendorAliases();   // lazy + once: only a rep who opens the picker pays for it
    vendorTargetIds = itemIds;
    vendorInput.value = '';
    vendorActiveIdx = -1;
    vendorDropdown.style.display = 'none';

    // Quick-pick pills from vendors already on the quote
    var seen = {};
    var quoteVendors = [];
    state.items.forEach(function(i) {
      if (i.vendor && i.vendor !== 'Custom' && !seen[i.vendor]) {
        // Don't include vendors from the items we're about to assign
        if (itemIds.indexOf(i.id) === -1) {
          seen[i.vendor] = true;
          quoteVendors.push(i.vendor);
        }
      }
    });
    if (quoteVendors.length) {
      vendorQuickPills.innerHTML = quoteVendors.map(function(v) {
        return '<button class="rq-ws-vendor-prompt__quick-pill" type="button">' + escHtml(v) + '</button>';
      }).join('');
      vendorQuickWrap.style.display = '';
    } else {
      vendorQuickWrap.style.display = 'none';
    }

    // Reset insert picker
    vendorInsertWrap.style.display = 'none';
    vendorPrompt.querySelector('.rq-ws-vendor-prompt__input-wrap').style.display = '';
    vendorPrompt.querySelector('.rq-ws-vendor-prompt__title').textContent = 'Vendor';

    vendorPrompt.style.display = '';
    setTimeout(function() { vendorInput.focus(); }, 50);
  }

  function closeVendorPrompt() {
    vendorPrompt.style.display = 'none';
    vendorTargetIds = [];
    vendorDropdown.style.display = 'none';
  }

  // Click backdrop to close vendor prompt
  var vendorBackdrop = vendorPrompt.querySelector('.rq-ws-vendor-prompt__backdrop');
  if (vendorBackdrop) {
    vendorBackdrop.addEventListener('click', closeVendorPrompt);
  }


  function applyVendor(name) {
    if (!name) name = 'Custom';

    /* ⛔ FOLD AN ALIAS BACK TO ITS VENDOR HERE, because this is the ONE funnel. The quick pills,
       the dropdown, Enter on a highlight, free-typed text, the Confirm button and the backdrop all
       arrive through this function, so resolving here cannot be bypassed by a route somebody adds
       later. Everything below this point, and every consumer downstream of it, sees the canonical
       name and nothing else. */
    var _canon = wsAliasToVendor[String(name).toLowerCase()];
    if (_canon && _canon !== name) {
      state.vendorLabels = state.vendorLabels || {};
      state.vendorLabels[_canon] = name;
      state.dirty = true;
      name = _canon;
    } else if (state.vendorLabels && state.vendorLabels[name]) {
      /* Picking the canonical name is how a rep takes the byline back off. */
      delete state.vendorLabels[name];
      state.dirty = true;
    }

    // Check if this vendor already has items with content (not blank rows)
    var existingItems = state.items.filter(function(i) {
      if (i.vendor !== name || vendorTargetIds.indexOf(i.id) !== -1) return false;
      var hasContent = (i.title || i.seriesBaseTitle || i.sku || i.msrp > 0 || i.cost > 0);
      return hasContent;
    });

    if (existingItems.length > 0) {
      showInsertPicker(name, existingItems);
      return;
    }

    finalizeVendor(name, -1);
  }

  function showInsertPicker(name, existingItems) {
    // Hide the vendor input area, show the insert picker
    vendorPrompt.querySelector('.rq-ws-vendor-prompt__quick').style.display = 'none';
    vendorPrompt.querySelector('.rq-ws-vendor-prompt__input-wrap').style.display = 'none';
    vendorDropdown.style.display = 'none';
    vendorPrompt.querySelector('.rq-ws-vendor-prompt__title').textContent = 'Insert after';

    var html = '<button class="rq-ws-vendor-prompt__insert-item rq-ws-vendor-prompt__insert-item--top" data-insert="top" type="button">Top of ' + escHtml(name) + '</button>';
    existingItems.forEach(function(item) {
      var label = item.title || item.seriesBaseTitle || item.sku || 'Untitled';
      html += '<button class="rq-ws-vendor-prompt__insert-item" data-insert="' + escAttr(item.id) + '" type="button">' + escHtml(label) + '</button>';
    });
    html += '<button class="rq-ws-vendor-prompt__insert-item rq-ws-vendor-prompt__insert-item--bottom" data-insert="bottom" type="button">Bottom of ' + escHtml(name) + '</button>';
    vendorInsertList.innerHTML = html;
    vendorInsertWrap.style.display = '';

    // Store vendor name for finalization
    vendorInsertWrap._vendorName = name;
  }

  vendorInsertList.addEventListener('click', function(e) {
    var btn = e.target.closest('.rq-ws-vendor-prompt__insert-item');
    if (!btn) return;
    var insertRef = btn.getAttribute('data-insert');
    var name = vendorInsertWrap._vendorName;
    finalizeVendor(name, insertRef);
  });

  function finalizeVendor(name, insertRef) {
    // Assign vendor to target items
    vendorTargetIds.forEach(function(id) {
      var item = findItem(id);
      if (item) item.vendor = name;
    });

    // Reposition items if an insertion point was chosen
    if (insertRef && insertRef !== -1) {
      // Remove target items from their current positions
      var targetItems = [];
      vendorTargetIds.forEach(function(id) {
        for (var i = state.items.length - 1; i >= 0; i--) {
          if (state.items[i].id === id) {
            targetItems.push(state.items.splice(i, 1)[0]);
            break;
          }
        }
      });
      targetItems.reverse(); // preserve original order

      // Find insertion index
      var insertIdx = state.items.length; // default: end
      if (insertRef === 'top') {
        // Before first item of this vendor
        for (var i = 0; i < state.items.length; i++) {
          if (state.items[i].vendor === name) { insertIdx = i; break; }
        }
      } else if (insertRef === 'bottom') {
        // After last item of this vendor
        for (var i = state.items.length - 1; i >= 0; i--) {
          if (state.items[i].vendor === name) { insertIdx = i + 1; break; }
        }
      } else {
        // After specific item
        for (var i = 0; i < state.items.length; i++) {
          if (state.items[i].id === insertRef) { insertIdx = i + 1; break; }
        }
      }

      // Insert target items at position
      for (var j = 0; j < targetItems.length; j++) {
        state.items.splice(insertIdx + j, 0, targetItems[j]);
      }
    }

    // Reset insert picker UI
    vendorInsertWrap.style.display = 'none';

    closeVendorPrompt();
    Q.renderItems();
    if (vendorPromptCallback) {
      var cb = vendorPromptCallback;
      vendorPromptCallback = null;
      cb();
    }
  }

  function renderVendorOptions(filter) {
    var vendors = getKnownVendors();
    var f = filter.toLowerCase();
    var filtered = f ? vendors.filter(function(v) { return v.toLowerCase().indexOf(f) !== -1; }) : vendors;
    if (!filtered.length) {
      vendorDropdown.style.display = 'none';
      vendorActiveIdx = -1;
      return;
    }
    vendorDropdown.innerHTML = filtered.map(function(v) {
      return '<div class="rq-ws-vendor-prompt__option">' + escHtml(v) + '</div>';
    }).join('');
    vendorDropdown.style.display = 'block';
    vendorActiveIdx = -1;
  }

  vendorQuickPills.addEventListener('click', function(e) {
    var pill = e.target.closest('.rq-ws-vendor-prompt__quick-pill');
    if (pill) applyVendor(pill.textContent);
  });

  vendorInput.addEventListener('input', function() {
    renderVendorOptions(this.value);
  });
  vendorInput.addEventListener('click', function() {
    if (vendorDropdown.style.display !== 'block') {
      renderVendorOptions(this.value);
    }
  });

  vendorDropdown.addEventListener('mousedown', function(e) {
    e.preventDefault();
    var opt = e.target.closest('.rq-ws-vendor-prompt__option');
    if (opt) applyVendor(opt.textContent);
  });

  vendorInput.addEventListener('keydown', function(e) {
    var opts = vendorDropdown.querySelectorAll('.rq-ws-vendor-prompt__option');
    if (e.key === 'ArrowDown' && opts.length) {
      e.preventDefault();
      vendorActiveIdx = vendorActiveIdx < opts.length - 1 ? vendorActiveIdx + 1 : 0;
      opts.forEach(function(o, i) { o.classList.toggle('rq-ws-vendor-prompt__option--active', i === vendorActiveIdx); });
    } else if (e.key === 'ArrowUp' && opts.length) {
      e.preventDefault();
      vendorActiveIdx = vendorActiveIdx > 0 ? vendorActiveIdx - 1 : opts.length - 1;
      opts.forEach(function(o, i) { o.classList.toggle('rq-ws-vendor-prompt__option--active', i === vendorActiveIdx); });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (vendorActiveIdx >= 0 && opts[vendorActiveIdx]) {
        applyVendor(opts[vendorActiveIdx].textContent);
      } else if (this.value.trim()) {
        applyVendor(this.value.trim());
      }
    } else if (e.key === 'Escape') {
      closeVendorPrompt();
    }
  });

  vendorPrompt.querySelector('.rq-ws-vendor-prompt__confirm').addEventListener('click', function() {
    var val = vendorInput.value.trim();
    if (!val) { vendorInput.focus(); return; }
    applyVendor(val);
  });
  vendorPrompt.querySelector('.rq-ws-vendor-prompt__backdrop').addEventListener('click', function() {
    var val = vendorInput.value.trim();
    if (!val) { vendorInput.focus(); return; }
    applyVendor(val);
  });

  /* ── Auto-format MSRP and Cost fields: $1,234.50 live ── */
  function liveFmtMoney(el) {
    if (!el) return;
    try {
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-27) — Allow negative entries so
       reps can type "-30" into Custom Fee for a one-off discount. Keep digits,
       decimal, AND minus; then collapse to a single LEADING minus (strip stray
       minuses elsewhere in the string). */
    var raw = el.value.replace(/[^0-9.\-]/g, '');
    var isNeg = raw.charAt(0) === '-';
    raw = raw.replace(/-/g, '');
    if (!raw || raw === '.') { el.value = isNeg ? '-' : ''; return; }
    // Only allow one decimal point
    var parts = raw.split('.');
    if (parts.length > 2) raw = parts[0] + '.' + parts.slice(1).join('');
    // Limit to 2 decimal places
    if (parts[1] && parts[1].length > 2) raw = parts[0] + '.' + parts[1].substring(0, 2);

    // Count digits before cursor to restore position
    var cursorPos = el.selectionStart;
    var digitsBefore = 0;
    var hitDecimal = false;
    for (var i = 0; i < cursorPos; i++) {
      var ch = el.value[i];
      if (/\d/.test(ch)) digitsBefore++;
      if (ch === '.') hitDecimal = true;
    }

    // Format: add commas to integer part, keep decimal as-is
    parts = raw.split('.');
    var intPart = parts[0] || '';
    var decPart = parts.length > 1 ? parts[1] : null;
    // Add commas
    var formatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var result = '$' + (isNeg ? '-' : '') + formatted + (decPart !== null ? '.' + decPart : '');

    el.value = result;

    // Restore cursor position by counting digits forward
    var count = 0;
    var newPos = 0;
    for (var j = 0; j < result.length; j++) {
      if (/\d/.test(result[j])) {
        count++;
        if (count === digitsBefore) { newPos = j + 1; break; }
      }
      if (result[j] === '.' && hitDecimal && count === digitsBefore) { newPos = j + 1; break; }
    }
    if (count < digitsBefore) newPos = result.length;
    if (digitsBefore === 0) newPos = result.indexOf('$') + 1;
    if (hitDecimal && result[newPos] === '.') newPos++;
    el.setSelectionRange(newPos, newPos);
    } catch(err) { console.error('[liveFmtMoney]', err); }
  }

  /* ── Row validation: highlight missing fields on partially filled rows ── */
  function validateRow(itemId) {
    var item = findItem(itemId);
    if (!item || item.type !== 'custom') return;
    var row = dom.itemsContainer.querySelector('tr[data-item-id="' + itemId + '"]');
    if (!row) return;

    // Check if row is "touched" — only count user-entered data, not defaults
    var titleVal = item.seriesId ? (item.seriesBaseTitle || '') : (item.title || '');
    var hasAny = titleVal || item.sku || (item.msrp > 0) || (item.cost > 0) || (item.quantity > 1);
    if (!hasAny) {
      row.querySelectorAll('.rq-ws-table__input--missing, .rq-ws-table__editable--missing').forEach(function(el) {
        el.classList.remove('rq-ws-table__input--missing', 'rq-ws-table__editable--missing');
      });
      return;
    }

    function mark(el, isMissing) {
      if (!el) return;
      var cls = el.classList.contains('rq-ws-table__editable') ? 'rq-ws-table__editable--missing' : 'rq-ws-table__input--missing';
      el.classList.toggle(cls, isMissing);
    }

    // Title
    var titleField = item.seriesId ? 'seriesBaseTitle' : 'title';
    mark(row.querySelector('[data-field="' + titleField + '"]'), !titleVal);
    // SKU
    var skuField = item.seriesId ? 'seriesSku' : 'sku';
    mark(row.querySelector('[data-field="' + skuField + '"]'), !item.sku);
    // Qty — check the actual input value, not state (state defaults to 1 but field shows blank)
    var qtyEl = row.querySelector('[data-field="quantity"]');
    mark(qtyEl, qtyEl && !qtyEl.value);
    // MSRP
    mark(row.querySelector('[data-field="msrp"]'), !item.msrp);
    // Cost
    mark(row.querySelector('[data-field="cost"]'), !item.cost);
    // Points — 0 is valid (sell at cost)
    mark(row.querySelector('[data-field="points"]'), item.points == null || item.points === '');
  }

  function validateAllRows() {
    state.items.forEach(function(item) {
      if (item.type === 'custom') validateRow(item.id);
    });
  }

  function hasValidationErrors() {
    return !!dom.itemsContainer.querySelector('.rq-ws-table__input--missing, .rq-ws-table__editable--missing');
  }

  // Validate previous row when entering a new one
  var lastValidateRowId = null;
  document.addEventListener('focusin', function(e) {
    var row = e.target.closest('tr[data-item-id]');
    var newId = row ? row.getAttribute('data-item-id') : null;
    if (lastValidateRowId && lastValidateRowId !== newId) {
      validateRow(lastValidateRowId);
    }
    lastValidateRowId = newId || lastValidateRowId;
  });
  // Also validate when clicking outside the items area entirely
  document.addEventListener('click', function(e) {
    if (lastValidateRowId && !dom.itemsContainer.contains(e.target)) {
      validateRow(lastValidateRowId);
      lastValidateRowId = null;
    }
  });

  // On blur: ensure cents are shown
  dom.itemsContainer.addEventListener('focusout', function(e) {
    var el = e.target;
    var field = el.getAttribute('data-field');
    if (field !== 'msrp' && field !== 'cost') return;
    var raw = parseMoney(el.value);
    el.value = raw ? fmt(raw) : '';
  });

  /* ── FREE toggle on right-click of item row ── */
  dom.itemsContainer.addEventListener('contextmenu', function(e) {
    var row = e.target.closest('tr[data-item-id]');
    if (!row) return;
    e.preventDefault();
    var itemId = row.getAttribute('data-item-id');
    var item = findItem(itemId);
    if (item) {
      item.isFree = !item.isFree;
      Q.renderItems();
    }
  });

  /* ── Auto-save to localStorage (every 30s when dirty) ── */
  function getCustomerData_forAutoSave() {
    var data = {};
    wrap.querySelectorAll('.rq-ws-customer__grid [data-field]').forEach(function(el) {
      data[el.getAttribute('data-field')] = el.value || '';
    });
    return data;
  }

  function doAutoSave() {
    if (!state.dirty || !state.items.length) return;
    try {
      var snapshot = {
        items: state.items,
        fees: state.fees,
        customFees: state.customFees,
        tax: state.tax,
        cardFee: state.cardFee,
        freight: state.freight,
        installation: state.installation,
        send: state.send,
        leadSource: state.leadSource,
        cfaAttribution: state.cfaAttribution,
        referral: state.referral, // CUSTOM_RFQPORTAL_BY_CLAUDE — BLOCK A shared recipient
        // BLOCK B is retired as an input (DNI decoupling piece 3) but still snapshotted so a
        // legacy value on an in-progress quote survives an autosave/restore cycle.
        referralSource: state.referralSource,
        ref: state.ref,
        customer: getCustomerData_forAutoSave(),
        _savedAt: Date.now(),
      };
      localStorage.setItem(getAutoSaveKey(), JSON.stringify(snapshot));
    } catch (e) { /* quota exceeded or private browsing */ }
  }

  setInterval(doAutoSave, 30000);
  window.addEventListener('beforeunload', function(e) {
    if (state.dirty && state.items.length) {
      doAutoSave();
      e.returnValue = 'You have unsaved changes.';
      return e.returnValue;
    }
  });

  // Clear auto-save after successful server save
  document.addEventListener('rq-ws:saved', function() {
    localStorage.removeItem(getAutoSaveKey());
  });

  /* ── Exports ── */
  Q.liveFmtMoney = liveFmtMoney;
  Q.validateAllRows = validateAllRows;
  Q.validateRow = validateRow;
  Q.hasValidationErrors = hasValidationErrors;
  Q.openVendorPrompt = openVendorPrompt;
  Q.propagateSeriesSku = propagateSeriesSku;
  Q.subdescBlurTimer = subdescBlurTimer;
  Q.openSeriesModal = openSeriesModal;
  Q.loadPendingQuote = loadPendingQuote;
})();
